import {
  Component, computed, ElementRef, HostListener,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Params, Router } from "@angular/router";
import * as d3 from "d3";
import { cloneDeep, debounce, range } from "lodash";
import {
  Observable,
  Subject,
  Subscription,
  animationFrameScheduler,
  forkJoin,
  fromEvent,
  of,
} from "rxjs";
import { auditTime, catchError, finalize, map, switchMap, take, takeUntil } from "rxjs/operators";
import { Cost } from "../../../models/cost";
import { CostItem } from "../../../models/costItem";
import { JobDetail } from "../../../models/jobDetail";
import { MinMaxDialog } from "../../../models/minMaxDialog";
import { PlotDetail } from "../../../models/plotDetail";
import { ShotOverlayDto } from "../../../models/shotOverlayDto";
import { ShotSettings } from "../../../models/shotSettings";
import { ShotType } from "../../../models/shotType";
import { SettingTypes } from "../../../shared/enums/settingTypes";
import { CachedProjectService } from "../../../shared/services/cached-project.service";
import { CachedUserService } from "../../../shared/services/cached-user.service";
import { DefaultMappingService } from "../../../shared/services/default-mapping.service";
import { ImageLoaderService } from "../../../shared/services/image-loader.service";
import { StoredSettingsService } from "../../services/stored-settings.service";
import { stripDash } from "../chart-slice/chart-slice.component";
import { VelocityComponent } from "../dialogs/velocity/velocity.component";
import { resizeBase64Img } from "./d3-image/d3-image.component";
import * as htmlToImage from "html-to-image";
import { ShotsGraphComponent } from "./shots-graph/shots-graph.component";
import { SpatialPlottingV2Component } from "./spatial-plotting-v2/spatial-plotting-v2.component";
import { WigglePlottingComponent } from "./wiggle-plotting/wiggle-plotting.component"
import { SourcePlottingComponent } from "./source-plotting/source-plotting.component";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { ScreenshotFrameService } from "../../../shared/services/screenshot-frame.service";
import { FormBuilder, FormGroup } from "@angular/forms";
import { toSignal } from "@angular/core/rxjs-interop";
import { Job } from "../../../models/job";
import { FavJobsService } from "../../services/fav-jobs.service";
import { BatchOutput, CapturedImage, downloadDataUrl, withBackgroundSafeRaf } from "../../../shared/batch-capture";
import { ShotBatchHost, ShotBatchScreenshotService, ShotBatchTarget } from "./shot-batch-screenshot.service";

export interface SpectrumResponse {
  predicted_x: number[];
  predicted_y: number[];
  field_x: number[];
  field_y: number[];
}

function getRandomHexColor(index: number = null): string {
  const first10Colors = [
    "#800080", // purple
    "#00FFFF", // cyan
    "#000000", // black
    "#FFFF00", // yellow
    "#FF00FF", // magenta
    "#FFA500", // orange
    "#00FF00", // green / lime
    "#0000FF", // blue
    "#FFFFFF", // white
    "#FF0000", // red
  ];

  if (index !== null && index < 10) {
    return first10Colors[index];
  }

  const hexChars = "0123456789ABCDEF";
  let color = "#";

  // Generate a random six-digit hexadecimal color code
  for (let i = 0; i < 6; i++) {
    color += hexChars[Math.floor(Math.random() * 16)];
  }

  return color;
}

@Component({
  selector: "app-shot-image",
  templateUrl: "./shot-image.component.html",
  styleUrls: ["./shot-image.component.less"],
  standalone: false
})
export class ShotImageComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>()
  @ViewChild("shots") shotsRef: ShotsGraphComponent;
  @ViewChild("rcvrGraph") rcvrGraphRef: ShotsGraphComponent;
  @ViewChild("rcvrGraphWrapper") rcvrGraphWrapper: ElementRef<HTMLDivElement>;
  @ViewChild("sourcePlotting") sourcePlotting: SourcePlottingComponent;
  @ViewChild("spatialPlottingV2Component")
  spatialPlottingV2: SpatialPlottingV2Component;
  @ViewChild("wigglePlottingComponent") wigglePlotting!: WigglePlottingComponent;
  settingsKey = "page";
  // parameters set on init
  iteration: number = 1;
  totalIteration: number;
  nx3: number;
  types: ShotType[];
  tableTypes: any[];
  typeDetails = [
    { name: "forward", value: 1 },
    { name: "test step", value: 2 },
  ];
  maxNumTracesShown: number = 1000;
  selectedJobs: any = null;
  isStaff = false;
  private _userSubscription: Subscription;
  // parameters for selected shot
  sliceNum: number;
  numTraces: number;
  numSamples: number;
  totalTime: number;

  // variables for dom rendering
  loadingImage: boolean = true;
  loadingShots: boolean = false;

  // variables for iteration switching
  switchIteration: number;
  itersOfSameShot: number[];

  spectrumDataType: object = { predicted: true, field: true };

  // variables for shot graph plotting
  private _shotData: any;
  get shotData(): any {
    return this._shotData;
  }
  set shotData(value: any) {
    this._shotData = value;
    // precomputed for the shot menu — filtering in the template binding would
    // re-run on every change-detection cycle
    this.shotsWithTraces = value ? value.filter((s) => s.hasTraceFile) : [];
  }
  shotsWithTraces: any[] = [];
  availableShots: number;
  unavailableShots: number;
  shotBgImg: string;
  selectedShot: number;
  selectedShotX: number;
  selectedShotY: number;
  defaultShotGraphHeight: number = 350;
  shotGraphHeight: number = this.defaultShotGraphHeight;
  defaultMainGraphHeight: number = 500;
  frequencyLimit: number = 50;
  mainGraphHeight: number = this.defaultMainGraphHeight;
  hasRightPage: boolean = false;
  hasLeftPage: boolean = false;
  shotColourTypes = [
    { id: "traces_fit", name: "Trace fit" },
    { id: "traces_raw", name: "Trace fit Raw" },
  ];
  private _selectedShotColourType: string = "traces_fit";
  get selectedShotColourType(): string {
    return this._selectedShotColourType;
  }
  set selectedShotColourType(value: string) {
    this._selectedShotColourType = value;
    this.selectedShotColourTypeName =
      this.shotColourTypes.find((i) => i.id == value)?.name || null;
  }
  selectedShotColourTypeName: string = "Trace fit";
  customName: string = null;

  // Custom dynamic filter settings
  customShotName = "1d";
  customShotSettings =
    '{ "filter_type": "raw", "noise": 0.01, "lag_max": 2000 }';
  shotImageError = null;

  //variables for retrieveing shot image
  panelNames: string[];
  tabNames: string[] = [];
  allPanelNames: string[] = ["Prediction", "Field", "Difference"];
  selectedPanel: number = 0;
  startTrace: number;
  endTrace: number;
  shotType: ShotType;
  imageUrl: string;
  clipValue: number[];
  interleave_extent: number = 10;
  isInterleaveType: boolean = false;
  currentScaling: number[] = [null, null];
  currentClipping: number = 0;
  globalClipping: boolean = true;
  setClipping = debounce(() => {
    this.storeSettings();
    // store scaling value for that shot type when changing the scaling bar
    this.shotTypeToScale[this.shotType.ttrName] = this.currentScaling;
    this.shotTypeToScale[`${this.shotType.ttrName}_clipping`] =
      this.currentClipping;
    this.updateMainImage();
  }, 250);

  //variable for runfile parameters
  blk_info: any = [];
  private _selectedBlk = null;
  get selectedBlk(): any {
    return this._selectedBlk;
  }
  set selectedBlk(value: any) {
    this._selectedBlk = value;
    // precomputed for the parameter panel — getParamValue() in the template
    // would re-run on every change-detection cycle
    this.blkParamValues = {
      freq: this.getParamValue("freq"),
      amplitude: this.getParamValue("amplitude"),
      agc: this.getParamValue("agc"),
      normalize: this.getParamValue("normalize"),
    };
  }
  blkParamValues = { freq: null, amplitude: null, agc: null, normalize: null };
  selectedTraceFit: number[];
  selectedShotFunctional: number;

  //variable for normalizing shot functional values
  typeOfSelectedRcvr: ShotType;
  scaleRatio: number;
  rcvrData: any;
  rcvrIndices: number[];
  rcvrDataFull: any;
  rcvrDataDisplay: any;
  rcvrScale = { min: 0, max: 100 };
  rcvrValue = 0;
  plotRcvr: boolean = true;
  selectRcvrs: boolean = false;

  rcvrInterval = 50;
  rcvrColorMap = ["blue", "white", "red"];
  rcvrAvg = 0;
  rcvrStd = 0;
  queryInterval = 1;

  sliderBarOpen: boolean = false;
  sliderBarVisible: boolean = false;
  is2D: boolean = false;
  // precomputed once per job — getModelScale() in a template binding would
  // allocate a fresh object on every change-detection cycle, forcing the
  // shots-graph child to re-render needlessly
  modelScale: { x: number; y: number };
  modelMax: { x: number; y: number };
  rangesStart = {
    x: signal<number>(0),
    y: signal<number>(0),
  };
  rangesEnd = {
    x: signal<number>(null),
    y: signal<number>(null),
  };
  xCrop = computed(() => [
    this.rangesStart.x(),
    this.rangesEnd.x(),
  ]);
  yCrop = computed(() => [
    this.rangesStart.y(),
    this.rangesEnd.y(),
  ]);
  synchroniseSettings: boolean = false;
  velocityAuto = true;
  //map that stores scaling values for diffrent shot type
  private shotTypeToScale = {};
  private shotTypeToAuto = {};
  private shotTypeToPalette = {};

  private jobId: string;
  private x2IsInline: boolean;

  private shotsImageSubscriber: Subscription;
  private mainImageSubscriber: Subscription;
  private selectedShotInfoSubscriber: Subscription;
  private shotsBgImageSubscriber: Subscription;
  private customNamesSubscriber: Subscription;
  private currentJobSubscriber: Subscription;
  private currentJob: JobDetail;
  private _routeQuerySubscription: Subscription;
  private delayedLoading;
  private loadedJob = false;
  private projectId = null;
  private _minValZ: number;
  private _maxValZ: number;
  private _synchoriseSubscription;
  private syncFwd: number = 1;
  startTraceManual: number;
  endTraceManual: number;

  pngWidth = 1600;
  pngHeight = 900;
  globalScaling = true;
  panelToScaling = {};
  panelToAuto = {};
  rcvrDistance: any;
  rcvrDistances: any;
  muteName = ["mute 1", "mute 2", "mute 3"];
  muteSlope = [1200, 1200, 1200];
  muteIntersection = [1.5, 1, 0.5];
  muteOn = [false, false, false]; // per mute
  colors = ["grey", "#00FF00", "yellow"];
  mute = false; // overall
  muteOffset = [0, 0, 0];
  muteDistanceTypes = [1, 1, 1];
  distanceTypes = [
    { name: "3D", value: 0 },
    { name: "Horizontal Plane", value: 1 },
    { name: "Vertical Inline Plane", value: 2 },
    { name: "Vertical Crossline Plane", value: 3 },
  ];
  shotColor = { min: 0, max: 100 }; // for shots graph color scale
  corr: number[] = [];
  gradientStyle = {};
  enable_corr: boolean = false;
  corr_start_time;
  corr_end_time;
  corr_mean;
  corr_std;
  start_time;
  end_time;
  color_map = d3
    .scaleSequential()
    .domain([-1, 1])
    .interpolator(d3.interpolateViridis);
  corrColorStyle = {
    background: `linear-gradient(to right, ${this.color_map(
      -1
    )}, ${this.color_map(-0.5)}, ${this.color_map(0)}, ${this.color_map(
      0.5
    )}, ${this.color_map(1)})`,
    height: "20px",
    width: "300px",
    "max-width": "30vw",
    "margin-bottom": "15px",
  };
  isMobile = false;
  is_updating_graph: boolean = false;
  scrollPos;
  time_to_scroll_subscription = new Subject<string>();
  phasediff_colorbar: string = null; // not needed anymore
  phasediff_scale_factor_power = 0.75;
  currentPhaseClipping = 1;
  // default_pdc = true; //default phasediff clipping -> no clipping
  phasediff_clipValue = 1; // the meta value returned with the phaseplot image call
  shotImageWidth: number; // for aligning the corr bar and the image
  isPhaseDiff: boolean = false;
  isSpectrum: boolean = false;
  showWiggle: boolean = false;
  spectrum: any; // an object with iteration as prop names and data as values, in the form of [predicted, field]
  spectrumOn: any; // an object with iteration as prop names and data as values, in the form of [true, false]
  spectrumColors: any; // an object with iteration as prop names and data as values, in the form of [color, color]
  spectrumRange: number[] = [null, null];
  spectrumRangeX: number[] = [null, null];
  shotGraphRadius: number = 3;
  functionalSequence; // for the itermenu of the spectrum graph to be based on iterblocks
  spectrumIterOptions: number[];
  // the current iter will always be blue and red
  currentIterSpectrumColors = ["blue", "red"];
  // the other iter take colors from this list, when exhausted, use random colors
  availableSpectrumColors = [
    "green",
    "yellow",
    "purple",
    "orange",
    "pink",
    "brown",
    "grey",
    "black",
  ];
  // recover available to this value when switching iters
  _spectrumColors = [
    "green",
    "yellow",
    "purple",
    "orange",
    "pink",
    "brown",
    "grey",
    "black",
  ];
  specInitialized = false;
  specInitSubscriber = new Subject<boolean>();
  inputRcvrScale = { min: 0, max: 100 };
  wasCorr = false; // used to determine whether to initialize the rcvrScale
  screenshotWithShotsInfo: boolean = false;
  screenshotWithSpatialPlotInfo: boolean = true;
  showWellLog: boolean = false;
  wellLogs: { x: number; y: number }[] = [];
  iterationFrequency: number = null;
  jobParameters: {} = null;
  showNA: boolean = true;
  selectedRcvrsData: object[] = [];
  selectedRcvrsLimit: any[] = [
    [0, 0],
    [0, 0],
  ];
  preserveAspectRatio: boolean = false;
  shotGraphWidth: string = "100%";
  // same treatment for the receiver spatial-plot map in the main column
  readonly defaultRcvrGraphWidth: string = "90%";
  preserveRcvrAspectRatio: boolean = false;
  rcvrGraphWidth: string = this.defaultRcvrGraphWidth;
  smoothingEnabled: boolean = false;
  color_palette:
    | "RED_WHITE_BLUE"
    | "BLUE_WHITE_RED"
    | "BLACK_AND_WHITE"
    | "WHITE_AND_BLACK" = "RED_WHITE_BLUE";
  showClippingModal: boolean = false;
  loadingCorr: boolean = false;
  correlation_mean_value: number | null = null;

  fieldScaling: number[] = [null, null];
  predictedScaling: number[] = [null, null];
  fieldClipping: number = 0;
  predictedClipping: number = 0;
  selectedShotLowcut: number[];
  settingsBottomOffset: number;

  wiggleForm: FormGroup;

  // ----- Batch Screenshots -----
  protected batchService = inject(ShotBatchScreenshotService);
  protected favJobsService = inject(FavJobsService);
  protected projectJobs = toSignal(
    inject(CachedProjectService).currentProject.pipe(map((p) => p?.jobs ?? [])),
    { initialValue: [] as Job[] },
  );
  protected settingsVisible = false;
  protected batchJobIds: string[] = [];
  protected batchShotId: number | null = null;
  // the current job's shots with trace files, snapshotted when the dialog opens
  protected batchShotOptions: number[] = [];
  protected batchOutput: BatchOutput = "zip";
  // set while a batch run drives the page: applied every time a job loads
  private batchTarget: ShotBatchTarget | null = null;

  constructor(
    public dialog: MatDialog,
    private imageService: ImageLoaderService,
    private projectService: CachedProjectService,
    private route: ActivatedRoute,
    private router: Router,
    private defaultMapping: DefaultMappingService,
    private storedSettingService: StoredSettingsService,
    private userService: CachedUserService,
    private notificationService: NzNotificationService,
    private zone: NgZone,
    private fb: FormBuilder,
    private screenshotFrame: ScreenshotFrameService,
  ) {
    this.wiggleForm = this.fb.group({
      gap: [10],
      negFill: [false],
      clip: [false],
    });
  }

  ngOnInit() {
    this.time_to_scroll_subscription.pipe(take(1)).subscribe((res) => {
      if (this.scrollPos != null) {
        window.scrollTo(0, this.scrollPos);
      }
    });
    this.isMobile = /Mobile/.test(navigator.userAgent) ||
      window.innerWidth <= 600 ||
      (window.innerHeight <= 600 && window.innerWidth > window.innerHeight);
    this.initScrollListener();
    if (this._routeQuerySubscription) {
      this._routeQuerySubscription.unsubscribe();
      this._routeQuerySubscription = null;
    }
    this._userSubscription = this.userService.userDetail.pipe(take(1)).subscribe((d) => {
      this.isStaff =
        d && (d.isStaff || (d.tracefitAccess != null && d.tracefitAccess));
    });
    this._routeQuerySubscription = this.route.queryParams.pipe(take(1)).subscribe(
      (params) => {
        this.updateFromQueryParams(params);
        this.time_to_scroll_subscription.next("LETS ROLL");
      }
    );

    this._synchoriseSubscription =
      this.storedSettingService.synchroniseSettings.pipe(takeUntil(this.destroy$)).subscribe(
        (val) => (this.synchroniseSettings = val)
      );
    // Get current job information and then get shot information for this job at iteration 1
    this.currentJobSubscriber = this.projectService.currentJob
      .pipe(
        takeUntil(this.destroy$),
        switchMap((job) => {
          this.specInitialized = false;
          this.storeSettings();
          this.initPage();
          if (!job) {
            return of(null);
          }
          this.currentJob = job;
          this.projectId = job.projectId;
          this.jobId = job.id;
          this.totalIteration = job.iterations;
          this.types = [
            {
              name: "lowpass-B4",
              ttrName: "lowpass-B4",
              numPanel: 2,
              displayName: "Raw",
              fwd: 1,
            },
            {
              name: "lowpass",
              ttrName: "lowpass",
              numPanel: 2,
              displayName: "Lowpass",
              fwd: 1,
            },
            {
              name: "agc",
              ttrName: "agc",
              numPanel: 3,
              displayName: "AGC",
              fwd: 1,
            },
            {
              name: "RLsmt",
              ttrName: "RLsmt",
              numPanel: 3,
              displayName: "REC Line Smooth",
              fwd: 1,
            },
            {
              name: "compare",
              ttrName: "compare",
              numPanel: 3,
              displayName: "Compare",
              fwd: 1,
            },
            {
              name: "res",
              ttrName: "res",
              numPanel: 1,
              displayName: "Residual",
              fwd: 1,
            },
            {
              name: "adj",
              ttrName: "adj",
              numPanel: 1,
              displayName: "Adjoint",
            },
            {
              name: "rwicut",
              ttrName: "rwicut",
              numPanel: 3,
              displayName: "RWI Cut",
              fwd: 1,
            },
            {
              name: "rwicut-B4",
              ttrName: "rwicut-B4",
              numPanel: 3,
              displayName: "RWI Cut Before",
              fwd: 1,
            },
            {
              name: "matchfilt",
              ttrName: "matchfilt",
              numPanel: 1,
              displayName: "Match Filter",
              fwd: 1,
            },
            {
              name: "primaries",
              ttrName: "primaries",
              numPanel: 1,
              displayName: "Primaries",
              fwd: 1,
            },
            {
              name: "multiples",
              ttrName: "multiples",
              numPanel: 1,
              displayName: "Multiples",
              fwd: 1,
            },
            {
              name: "matchmults",
              ttrName: "matchmults",
              numPanel: 1,
              displayName: "Multiples Matched",
              fwd: 1,
            },
            {
              name: "direct-arrival",
              ttrName: "direct-arrival",
              numPanel: 1,
              displayName: "Direct Arrival",
              fwd: 1,
            },
            {
              name: "fullfield",
              ttrName: "fullfield",
              numPanel: 1,
              displayName: "Full Field",
              fwd: 1,
            },
            {
              name: "fullpred",
              ttrName: "fullpred",
              numPanel: 1,
              displayName: "Full Predicted",
              fwd: 1,
            },
            {
              name: "1d",
              ttrName: "compare",
              numPanel: 1,
              displayName: "1D Dynamic",
              fwd: 1,
              filterName: "1d",
              filterSettings: {
                filter_type: "raw",
                noise: 0.01,
                lag_max: 2000,
                normalise: "True",
                lowpass_on: "True",
                lowpass_cutoff: 4.5,
                lowpass_order: 6,
                t_scale: 0.01,
                t_type: "Gaussian",
                wmin: "True",
              },
              options: [
                {
                  name: "filter_type",
                  displayName: "Filter Type",
                  values: ["raw", "adjoint", "residual", "check"],
                },
                { name: "noise", displayName: "Noise" },
                { name: "lag_max", displayName: "Lag Max" },
                {
                  name: "normalise",
                  displayName: "Normalise",
                  values: ["True", "False"],
                },
                {
                  name: "lowpass_on",
                  displayName: "Lowpass",
                  values: ["True", "False"],
                },
                { name: "lowpass_cutoff", displayName: "Lowpass Cutoff" },
                { name: "lowpass_order", displayName: "Lowpass Order" },
                { name: "t_scale", displayName: "T Scale" },
                {
                  name: "t_type",
                  displayName: "T Type",
                  values: ["Gaussian", "Linear", "Exp"],
                },
                {
                  name: "wmin",
                  displayName: "wmin",
                  values: ["True", "False"],
                },
              ],
            },
            {
              name: "2d",
              ttrName: "compare",
              numPanel: 1,
              displayName: "2D Dynamic",
              fwd: 1,
              hideAxes: true,
              filterName: "2d",
              filterSettings: {
                filter_type: "raw",
                sample_interval: 10,
                lag_t_max: 150,
                lag_x_max: 200,
                noise: 0.01,
                t_scale_x: 0.1,
                t_scale_t: 0.3,
                t_type: "Gaussian",
                wmin: "True",
              },
              options: [
                {
                  name: "filter_type",
                  displayName: "Filter Type",
                  values: ["raw", "adjoint", "residual"],
                },
                { name: "sample_interval", displayName: "Sample Interval" },
                { name: "lag_t_max", displayName: "Lag t_max" },
                { name: "lag_x_max", displayName: "Lag x_max" },
                { name: "noise", displayName: "Noise" },
                { name: "t_scale_x", displayName: "T Scale x" },
                { name: "t_scale_t", displayName: "T Scale t" },
                {
                  name: "t_type",
                  displayName: "T Type",
                  values: ["Gaussian", "Spike"],
                },
                {
                  name: "wmin",
                  displayName: "wmin",
                  values: ["True", "False"],
                },
              ],
            },
            {
              name: "Source",
              ttrName: "src",
              displayName: "Source",
              numPanel: 1,
              fwd: 1,
              independent: true,
            },
            {
              name: "RcvrList",
              ttrName: "rcvrlist",
              numPanel: 3,
              displayName: "Spatial Plotting",
              fwd: 1,
              filterSettings: {
                freq: 3,
                offset: 2000,
                angles: "0, 90, 180, 270",
                angle_offset: 25,
                value: "freq",
                interval: 10,
              },
              options: [
                {
                  name: "value",
                  displayName: "Receiver Value",
                  values: ["freq", "res", "corr"],
                },
                { name: "freq", displayName: "frequency" },
                { name: "offset", displayName: "Offset" },
                { name: "angles", displayName: "Angles", type: "text" },
                { name: "angle_offset", displayName: "Angle Offset" },
                { name: "interval", displayName: "Interval" },
              ],
            },
            {
              name: "SelectRcvrs",
              independent: true,
              ttrName: "compare",
              numPanel: 2,
              displayName: "Spatial Plotting V2",
              fwd: 1,
              filterSettings: {
                bounding_box: [
                  [0, 0],
                  [0, 0],
                ],
                image_scaling: 1,
                // interaleave_extent: "10",
                panel: "Prediction",
                rcvr_interval: 10,
                sort_dir: "asc",
                sort_ind: "id",
              },
            },
            {
              name: "custom",
              ttrName: "compare",
              numPanel: 1,
              displayName: "Custom Dynamic",
              fwd: 1,
            },
            {
              name: "custom_type",
              ttrName: "custom",
              numPanel: 3,
              displayName: "Custom Shot Type",
              options: null,
            },
          ];
          this.tableTypes = this.transTableTypes(12);
          if (!this.restoreSettings()) {
            this.iteration = Math.min(this.totalIteration, this.iteration);
          }
          if (this.iteration > this.totalIteration) {
            this.iteration = this.totalIteration;
          }
          // a batch run overrides whatever shot/iteration was restored for this job
          this.applyBatchTarget();
          if (!this.typeOfSelectedRcvr) {
            this.typeOfSelectedRcvr = this.types[11];
          }
          if (this.shotType) {
            //this.shotType = this.types.find(t => t.name==this.shotType.name)
          } else {
            // update shottype
            this.shotType = this.types[0];
            this.shotTypeToColorType();
          }
          this.x2IsInline = job.x2_is_inline;
          this.nx3 = job.modelGrid.nx3;
          this.is2D = job.modelGrid.is2d;
          this.modelScale = this.getModelScale();

          this.modelMax = {
            x: this.modelScale.x,
            y: this.modelScale.y
          }
          this.restoreRanges();
          if (!this.sliceNum && this.nx3) {
            this.sliceNum = Math.floor(this.nx3 / 2);
          }
          if (!this.loadedJob) {
            this.updateFromQueryParams(this.route.snapshot.queryParams);
          }else{
            this.setPanelNames();
            this.updateShotsGraph()
          }

          if (this.synchroniseSettings) {
            this.shotType.fwd = this.syncFwd;
          }

          this.loadedJob = true;

          this.getJobParameters();

          // Calculate width if preserve aspect ratio is enabled
          if (this.preserveAspectRatio) {
            setTimeout(() => {
              this.calculateWidthFromHeight();
              this.updateRef();
            }, 100);
          } else {
            // Ensure width is reset to 100% if aspect ratio is not preserved
            this.shotGraphWidth = "100%";
          }

          // ...and the same for the receiver spatial-plot map: the grid it
          // takes its ratio from belongs to the job that just loaded
          if (this.preserveRcvrAspectRatio) {
            setTimeout(() => {
              this.calculateRcvrWidthFromHeight();
              this.redrawRcvrGraph();
            }, 100);
          } else {
            this.rcvrGraphWidth = this.defaultRcvrGraphWidth;
          }

          // this.specInit()
          return this.projectService.getJobMeta(this.jobId);
        })
      )
      .subscribe((meta) => {
        this.checkIsPhaseDiff();
        this.checkIsSpectrum();
        if (!meta) {
          this.blk_info = [];
          this.selectedBlk = null;
          return of(null);
        }
        this.blk_info = meta.blk_data;
        if (!this.selectedBlk) {
          this.selectedBlk =
            this.blk_info?.length > 0 ? this.blk_info[0] : null;
        }
        this.ensureSelectedPanelExists();
      });
  }

  getJobParameters() {
    this.projectService
      .getParameterTable(this.projectId, false, [this.jobId])
      .pipe(take(1))
      .subscribe((res) => {
        const jobParams = res["results"].length > 0 ? res["results"][0] : {};
        this.jobParameters = jobParams;
        this.setIterationFrequency();
      });
  }
  setIterationFrequency() {
    let iters = this.jobParameters["iters"]
      ? Number(this.jobParameters["iters"])
      : null;
    let freq = this.jobParameters["frequency"]
      ? JSON.parse(this.jobParameters["frequency"])
      : null;

    if (!iters || !freq) {
      this.iterationFrequency = null;
      return;
    }

    let iteration = this.iteration;
    if (iteration === 0) {
      this.iterationFrequency = freq[0];
      this.selectedBlk = this.blk_info[0];
    }
    if (iters && freq) {
      // Parse the iterations string into an array
      let iterationsArray = [];
      try {
        // Replace '-' with null for valid JSON parsing
        const iterationsString = this.jobParameters["iterations"]
          .replace(/'/g, '"')
          .replace(/-/g, "null");
        iterationsArray = JSON.parse(iterationsString);
      } catch (e) {
        iterationsArray = [];
      }

      // Build array of actual iterations per block (considering overrides)
      const actualIterationsPerBlock = [];
      for (let i = 0; i < freq.length; i++) {
        const overrideValue = iterationsArray[i];
        if (
          overrideValue !== null &&
          overrideValue != null &&
          !isNaN(overrideValue)
        ) {
          actualIterationsPerBlock.push(Number(overrideValue));
        } else {
          actualIterationsPerBlock.push(iters);
        }
      }

      // Find which block the current iteration falls into
      let cumulativeIterations = 0;
      let blockIndex = 0;
      for (let i = 0; i < actualIterationsPerBlock.length; i++) {
        const blockIterations = actualIterationsPerBlock[i];

        if (iteration <= cumulativeIterations + blockIterations) {
          blockIndex = i;
          break;
        }
        cumulativeIterations += blockIterations;
      }

      this.iterationFrequency = freq[blockIndex] || null;
      this.selectedBlk = this.blk_info[blockIndex] || null;
    }
  }

  private ensureSelectedPanelExists() {
    const selectedPanelName = this.panelNames[this.selectedPanel];
    if (!this.panelNames.includes(selectedPanelName)) {
      const predictionPanelIndex = this.panelNames.indexOf("Prediction");
      if (predictionPanelIndex !== -1) {
        this.selectedPanel = predictionPanelIndex;
      } else {
        this.selectedPanel = 0;
      }
    }
  }

  private isEmpty(obj) {
    for (var prop in obj) {
      if (obj.hasOwnProperty(prop)) return false;
    }

    return true;
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()

    this.storeSettings();
    this.loadedJob = false;
    if (
      this.currentJobSubscriber !== null &&
      this.currentJobSubscriber !== undefined
    ) {
      this.currentJobSubscriber.unsubscribe();
      this.currentJobSubscriber = null;
    }
    if (
      this.mainImageSubscriber !== null &&
      this.mainImageSubscriber !== undefined
    ) {
      this.mainImageSubscriber.unsubscribe();
      this.mainImageSubscriber = null;
    }
    if (
      this.shotsBgImageSubscriber !== null &&
      this.shotsBgImageSubscriber !== undefined
    ) {
      this.shotsBgImageSubscriber.unsubscribe();
      this.shotsBgImageSubscriber = null;
    }
    if (
      this.shotsImageSubscriber !== null &&
      this.shotsImageSubscriber !== undefined
    ) {
      this.shotsImageSubscriber.unsubscribe();
      this.shotsImageSubscriber = null;
    }
    if (
      this.selectedShotInfoSubscriber !== null &&
      this.selectedShotInfoSubscriber !== undefined
    ) {
      this.selectedShotInfoSubscriber.unsubscribe();
      this.selectedShotInfoSubscriber = null;
    }

    if (
      this._routeQuerySubscription !== null &&
      this._routeQuerySubscription !== undefined
    ) {
      this._routeQuerySubscription.unsubscribe();
      this._routeQuerySubscription = null;
    }
    if (
      this._userSubscription !== null &&
      this._userSubscription !== undefined
    ) {
      this._userSubscription.unsubscribe();
      this._userSubscription = null;
    }
    if (
      this.time_to_scroll_subscription !== null &&
      this.time_to_scroll_subscription !== undefined
    ) {
      this.time_to_scroll_subscription.unsubscribe();
      this.time_to_scroll_subscription = null;
    }
  }

  /**
   * Scroll handling runs outside the Angular zone with rAF throttling: the
   * old @HostListener version triggered full change detection (and layout
   * reads) on every scroll event, which made the whole page feel sluggish.
   * Change detection only runs when a template-bound value actually flips.
   */
  private initScrollListener() {
    this.zone.runOutsideAngular(() => {
      fromEvent(window, "scroll", { passive: true })
        .pipe(auditTime(0, animationFrameScheduler), takeUntil(this.destroy$))
        .subscribe(() => this.onWindowScroll());
    });
  }

  private onWindowScroll() {
    const sliceSettings = document.getElementById("slice-settings");
    const offsetTop = sliceSettings
      ? sliceSettings.getBoundingClientRect().top + window.scrollY
      : null;
    const sliderBarVisible =
      offsetTop != null && offsetTop + 50 < window.scrollY;
    // only used for scroll restore / settings persistence — no CD needed
    this.scrollPos = window.scrollY || 0;

    const jobTabsBottom = document.getElementById("job-tabs-bottom");
    const settingsBottomOffset = jobTabsBottom
      ? jobTabsBottom.offsetHeight
      : this.settingsBottomOffset;

    // these two are template-bound; re-enter the zone only when they change
    if (
      sliderBarVisible != this.sliderBarVisible ||
      settingsBottomOffset != this.settingsBottomOffset
    ) {
      this.zone.run(() => {
        this.sliderBarVisible = sliderBarVisible;
        this.settingsBottomOffset = settingsBottomOffset;
      });
    }
  }

  @HostListener("window:resize", ["$event"])
  onWindowResize(e) {
    this.resizeWithDelay();
  }

  private resizeWithDelay = debounce(() => {

    // Recalculate width if preserve aspect ratio is enabled
    if (this.preserveAspectRatio && this.currentJob) {
      // Keep height, recalculate width
      this.calculateWidthFromHeight();
      this.updateRef();
    }
  }, 150);

  // Event handlers
  onIterationChange(): void {
    this.setIterationFrequency();
    this.storeSettings();
    this.initPage();
    this.updateShotsGraph();
    this.updateUrl();
  }

  onFrequencyLimitChange(event: KeyboardEvent) {
    if (event.key == "Enter" && this.sourcePlotting) {
      this.sourcePlotting.frequencyLimit = this.frequencyLimit;
      this.sourcePlotting.onFrequencyLimitChanged();
    }
  }

  onShowWiggleChange() {
    let lastPanelName = this.panelNames[this.selectedPanel];
    this.setPanelNames();
    let newIndex = this.panelNames.findIndex((pN) => pN == lastPanelName);
    this.selectedPanel = newIndex > -1 ? newIndex : 0;
  }

  onShotTypeChange(value: ShotType, event: Event) {
    // this.mute = false;
    // this.rcvrDistance = null;
    // this.rcvrDistances = [];
    event.stopPropagation();

    // Store current shot type settings before switching
    this.storeShotTypeSettings();
    // update shot type
    this.shotType = value;
    this.shotTypeToColorType();
    if (this.synchroniseSettings) {
      this.shotType.fwd = this.syncFwd;
    }

    // Restore settings for the new shot type
    this.restoreShotTypeSettings();

    this.storeSettings();
    let lastPanelName = this.panelNames[this.selectedPanel];
    this.setPanelNames();
    let newIndex = this.panelNames.findIndex((pN) => pN == lastPanelName);

    // console.log(newIndex)
    // if (this.shotType.numPanel <= this.selectedPanel) {
    //   this.selectedPanel = 0;
    // }

    if (newIndex != null && newIndex > -1) {
      this.selectedPanel = newIndex;
    } else {
      this.selectedPanel = 0;
    }
    if (this.selectedShot) {
      this.updateSelectedShotInfo();
    }
    if (value.name == "RcvrList") {
      this.typeOfSelectedRcvr = value;
    }
    if (value.name != "SelectRcvrs") {
      this.selectRcvrs = false;
    }
    this.updateUrl();
  }

  private storeShotTypeSettings() {
    if (!this.shotType) return;

    const shotTypeKey = this.shotType.ttrName;

    // Store scaling and clipping values
    this.shotTypeToScale[shotTypeKey] = [...this.currentScaling];
    this.shotTypeToScale[`${shotTypeKey}_clipping`] = this.currentClipping;

    // Store auto settings
    this.shotTypeToAuto[shotTypeKey] = this.velocityAuto;

    // Store panel-specific settings for this shot type
    this.shotTypeToScale[`${shotTypeKey}_fieldScaling`] = [
      ...this.fieldScaling,
    ];
    this.shotTypeToScale[`${shotTypeKey}_predictedScaling`] = [
      ...this.predictedScaling,
    ];
    this.shotTypeToScale[`${shotTypeKey}_fieldClipping`] = this.fieldClipping;
    this.shotTypeToScale[`${shotTypeKey}_predictedClipping`] =
      this.predictedClipping;
    this.shotTypeToScale[`${shotTypeKey}_phaseDiffClipping`] =
      this.currentPhaseClipping;

    // Store global scaling setting for this shot type
    this.shotTypeToScale[`${shotTypeKey}_globalScaling`] = this.globalScaling;

    // Store color palette for this shot type
    this.shotTypeToPalette[shotTypeKey] = this.color_palette;
  }

  private restoreShotTypeSettings() {
    if (!this.shotType) return;

    const shotTypeKey = this.shotType.ttrName;

    // Restore scaling and clipping values
    this.currentScaling = this.shotTypeToScale[shotTypeKey] || [null, null];
    this.currentClipping = this.shotTypeToScale[`${shotTypeKey}_clipping`] || 0;

    // Restore auto settings
    this.velocityAuto =
      this.shotTypeToAuto[shotTypeKey] !== undefined
        ? this.shotTypeToAuto[shotTypeKey]
        : true;

    // Restore panel-specific settings
    this.fieldScaling = this.shotTypeToScale[`${shotTypeKey}_fieldScaling`] || [
      null,
      null,
    ];
    this.predictedScaling = this.shotTypeToScale[
      `${shotTypeKey}_predictedScaling`
    ] || [null, null];
    this.fieldClipping =
      this.shotTypeToScale[`${shotTypeKey}_fieldClipping`] || 0;
    this.predictedClipping =
      this.shotTypeToScale[`${shotTypeKey}_predictedClipping`] || 0;
    this.currentPhaseClipping =
      this.shotTypeToScale[`${shotTypeKey}_phaseDiffClipping`] || 0;

    // Restore global scaling setting
    const savedGlobalScaling =
      this.shotTypeToScale[`${shotTypeKey}_globalScaling`];
    if (savedGlobalScaling !== undefined) {
      this.globalScaling = savedGlobalScaling;
    }

    // Restore color palette for this shot type
    this.color_palette =
      this.shotTypeToPalette[shotTypeKey] || "RED_WHITE_BLUE";

    this.panelToScaling["Field"] = [...this.fieldScaling];
    this.panelToScaling["Prediction"] = [...this.predictedScaling];
    this.panelToAuto["Field"] = this.velocityAuto;
    this.panelToAuto["Prediction"] = this.velocityAuto;
    this.panelToAuto["Interleaved"] = this.velocityAuto;

    // Update panel scaling based on current panel
    if (
      this.panelNames &&
      this.panelNames.length > 0 &&
      this.panelNames[this.selectedPanel] != "Phase Difference"
    ) {
      this.currentScaling =
        this.panelToScaling[this.panelNames[this.selectedPanel]] ||
        this.currentScaling;
    }
  }

  onShotTypeDetailChange(value: number, event) {
    this.mute = false;
    this.rcvrDistance = null;
    this.rcvrDistances = [];
    event.stopPropagation();
    this.shotType.fwd = value;
    if (this.selectedShot) {
      this.updateSelectedShotInfo();
    }

    if (this.synchroniseSettings) {
      this.syncFwd = value;
    }
  }

  onIterationSwitch(iteration?: number): void {
    let newIter = iteration || this.switchIteration;
    this.switchIteration = this.iteration;
    this.iteration = newIter;
    this.setIterationFrequency();
    this.updateShotsGraph();
    this.updateUrl();
  }

  onShotSelected(shotId: number) {
    if (!this.shotData) {
      return;
    }
    if (this.selectedShot === shotId) {
      return;
    }
    let shot = this.shotData.find((s) => s.shotId == shotId);
    this.selectedTraceFit = [Number(shot.traces_fit), Number(shot.traces_raw)];
    this.selectedShotLowcut = shot?.lowcut || null;
    this.selectedShotFunctional = Number(
      (shot.fnlc / this.scaleRatio).toFixed(2)
    );
    this.selectedShotX = shot.x;
    this.selectedShotY = shot.y;
    if (!shot.hasTraceFile) {
      this.selectedShot = shotId;
      this.storeSettings();
      this.imageUrl = null;
      this.loadingImage = false;
      return;
    }
    this.selectedShot = shotId;
    this.loadingImage = true;
    if (this.shotType.name == "RcvrList") {
      this.typeOfSelectedRcvr = this.shotType;
    }

    this.storeSettings();
    this.updateUrl();
    this.updateShotsGraph()
  }

  onReplotClicked() {
    // this.startTrace = Math.min(this.numTraces-1, Math.max(this.startTrace, 1))
    // this.endTrace = Math.max(this.startTrace+1, Math.min(this.numTraces, this.endTrace))
    this.startTrace = Math.max(1, this.startTrace);
    this.endTrace = Math.min(this.numTraces ?? Infinity, this.endTrace);
    if (this.startTrace >= this.endTrace) {
      this.startTrace = Math.max(1, this.endTrace - this.maxNumTracesShown);
    }
    // In receiver spatial-plotting mode the visible plot is the receiver
    // scatter, whose point colours come straight from _getRcvrData (the
    // receiver value / correlation the user just changed). updateMainImage()'s
    // "only if not already loaded" guard would skip the refetch once
    // rcvrDataFull exists, so the new filter settings would never reach the
    // server and the colours would appear frozen until a job switch rebuilt
    // the component. Refetch explicitly here for that mode.
    if (
      this.shotType.name == "RcvrList" &&
      this.typeOfSelectedRcvr.name == "RcvrList"
    ) {
      this._getRcvrData(true);
    } else {
      this.updateMainImage();
    }
    this.wigglePlotting?.submitTraceRange(
      this.startTrace,
      this.endTrace,
      this.start_time,
      this.end_time
    );
    this.setClipping();
    this.updateUrl();
  }

  onTabSwitch() {
    this.checkIsInterleaved();
    this.checkIsPhaseDiff();
    this.checkIsSpectrum();

    if (!this.isPhaseDiff) {
      this.currentScaling = this.panelToScaling[
        this.panelNames[this.selectedPanel]
      ] || [null, null];
      if (this.isInterleaveType) {
        this.velocityAuto =
          this.panelToAuto["Interleaved"] || this.panelToAuto["Prediction"];
      } else {
        this.velocityAuto =
          this.panelToAuto[this.panelNames[this.selectedPanel]] ||
          (this.currentScaling[0] == null && this.currentScaling[1] == null);
      }
    }

    if (
      !this.isPhaseDiff &&
      this.currentScaling[0] !== this.currentScaling[1]
    ) {
      this.currentScaling[0] = -this.currentScaling[1];
    }

    // confirm that the predictedScaling and fieldScaling is the same on both indices
    if (this.fieldScaling[0] !== this.fieldScaling[1]) {
      this.fieldScaling[0] = -this.fieldScaling[1];
    }
    if (this.predictedScaling[0] !== this.predictedScaling[1]) {
      this.predictedScaling[0] = -this.predictedScaling[1];
    }

    if (this.velocityAuto && this.globalScaling) {
      this.fieldClipping = this.currentClipping;
      this.predictedClipping = this.currentClipping;
    }

    this.syncClipValueForSelectedPanel();

    this.updateMainImage();
  }

  checkIsPhaseDiff() {
    if (this.panelNames == null || this.panelNames.length == 0) return false;
    this.isPhaseDiff =
      this.panelNames[this.selectedPanel] == "Phase Difference";
    return this.isPhaseDiff;
  }

  checkIsSpectrum() {
    if (this.panelNames == null || this.panelNames.length == 0) return false;
    this.isSpectrum = this.panelNames[this.selectedPanel] == "Spectrum";
    return this.isSpectrum;
  }

  checkIsInterleaved() {
    this.isInterleaveType =
      this.panelNames[this.selectedPanel] == "Interleaved";
  }

  onDepthChange() {
    this.storeSettings();
    this.updateBgRange();
  }

  onSliceChange(next: boolean) {
    let range = this.endTrace - this.startTrace;
    if (next) {
      this.endTrace = Math.min(this.numTraces ?? Infinity, this.endTrace + range);
      this.startTrace = this.endTrace - range;
    } else {
      this.startTrace = Math.max(1, this.startTrace - range);
      this.endTrace = this.startTrace + range;
    }
    this.updateMainImage();
    this.updateUrl();
    this.setClipping();
  }

  // Wrapper for keyboard event
  onKeySubmission(event: KeyboardEvent) {
    this.checkStartTimeEndTime();
    if (this.shotType.name == "Source") return;

    this.checkPhaseDiffScale();
    if (event.key == "Enter") {
      if (this.enable_corr) {
        if (this.start_time) {
          this.corr_start_time = Math.max(
            this.corr_start_time,
            this.start_time
          );
        } else {
          this.corr_start_time = Math.max(this.corr_start_time, 0);
        }

        if (this.end_time) {
          this.corr_end_time = Math.min(this.corr_end_time, this.end_time);
        } else {
          this.corr_end_time = Math.min(this.corr_end_time, this.totalTime);
        }
      }
      this.onReplotClicked();
    }
  }

  onPreserveAspectRatioChange(checked: boolean) {
    if (checked) {
      // Check if the calculated width would overflow
      if (!this.checkWidthOverflow()) {
        this.preserveAspectRatio = false;
        this.notificationService.error(
          "Error",
          "Cannot enable preserve aspect ratio: The calculated width would overflow into the right column. Please reduce the height or resize the window."
        );
        return;
      }
      // Keep current height and calculate width to match aspect ratio
      this.calculateWidthFromHeight();
    } else {
      // Reset to full width when unchecked
      this.shotGraphWidth = "100%";
    }
    // Change slightly to trigger re-render
    this.shotGraphHeight = this.shotGraphHeight + 1;
    setTimeout(() => {
      this.shotGraphHeight = this.shotGraphHeight - 1;
      this.updateRef();
    }, 10);
  }

  onHeightSliderChange() {
    if (
      this.preserveAspectRatio &&
      this.currentJob &&
      this.currentJob.modelGrid
    ) {
      // Calculate width based on the new height to maintain aspect ratio
      this.calculateWidthFromHeight();
      this.updateRef();
    }
  }

  calculateWidthFromHeight() {
    const calculatedWidth = this.aspectWidthFor(this.shotGraphHeight);
    if (calculatedWidth == null) {
      return;
    }

    this.shotGraphWidth = `${calculatedWidth}px`;
  }

  // Width a map of the given height needs for the model's crossline/inline
  // proportions to survive: 3D model, crossline (nx2) is X, inline (nx1) is Y.
  // Shared by the shots map and the receiver spatial-plot map.
  private aspectWidthFor(height: number): number | null {
    if (!this.currentJob || !this.currentJob.modelGrid) {
      return null;
    }

    // inline (nx1), crossline (nx2), depth (nx3)
    const { nx1, nx2 } = this.currentJob.modelGrid;

    return Math.round(height * (nx2 / nx1));
  }

  onPreserveRcvrAspectRatioChange(checked: boolean) {
    if (checked) {
      if (!this.checkRcvrWidthOverflow()) {
        this.preserveRcvrAspectRatio = false;
        this.notificationService.error(
          "Error",
          "Cannot enable preserve aspect ratio: The calculated width would overflow the image column. Please reduce the height or resize the window."
        );
        return;
      }
      this.calculateRcvrWidthFromHeight();
    } else {
      this.rcvrGraphWidth = this.defaultRcvrGraphWidth;
    }
    this.redrawRcvrGraph();
  }

  onMainHeightSliderChange() {
    if (this.preserveRcvrAspectRatio) {
      // no explicit redraw needed: the new height reaches the graph in the same
      // change-detection pass as the new width, and it re-measures its
      // container when it redraws for the height
      this.calculateRcvrWidthFromHeight();
    }
  }

  calculateRcvrWidthFromHeight() {
    const calculatedWidth = this.aspectWidthFor(this.mainGraphHeight);
    if (calculatedWidth == null) {
      return;
    }

    this.rcvrGraphWidth = `${calculatedWidth}px`;
  }

  // The receiver map is rendered, so its column can be measured directly
  // instead of approximated from the viewport as checkWidthOverflow() does.
  private checkRcvrWidthOverflow(): boolean {
    const calculatedWidth = this.aspectWidthFor(this.mainGraphHeight);
    if (calculatedWidth == null) {
      return false;
    }

    const column = this.rcvrGraphWrapper?.nativeElement?.parentElement;
    if (!column) {
      return true;
    }

    return calculatedWidth <= column.clientWidth;
  }

  // Re-measuring the container is the child's job, but only a height change
  // makes it redo it — a width-only change (toggling the ratio) needs a nudge
  // once the new style has landed.
  private redrawRcvrGraph() {
    setTimeout(() => this.rcvrGraphRef?.updateGraph(), 50);
  }

  checkWidthOverflow(): boolean {
    if (!this.currentJob || !this.currentJob.modelGrid) {
      return false;
    }

    // inline (nx1), crossline (nx2)
    const { nx1, nx2 } = this.currentJob.modelGrid;

    const calculatedWidth = Math.round(this.shotGraphHeight * (nx2 / nx1));

    // Approximate available width:
    // Get container width - col-md-7 is 58.33% of parent
    // The parent is col-md-10 which is 83.33% of viewport
    // So col-md-7 is roughly 58.33% of 83.33% = 48.6% of viewport
    const viewportWidth = window.innerWidth;
    const containerWidth = viewportWidth * 0.833 * 0.583;

    // Add buffer for padding and margins (20px on each side)
    const availableWidth = containerWidth - 40;

    return calculatedWidth <= availableWidth;
  }

  submitSpectrumRange(event: KeyboardEvent) {
    if (event.key == "Enter") {
      this.updateRef();
    }
  }

  submitCorrRange(event: KeyboardEvent) {
    if (event.key == "Enter") {
      if (this.inputRcvrScale.min == null) {
        this.inputRcvrScale.min = this.rcvrScale.min;
      }
      if (this.inputRcvrScale.max == null) {
        this.inputRcvrScale.max = this.rcvrScale.max;
      }
      this.rcvrScale = {
        min: this.inputRcvrScale.min,
        max: this.inputRcvrScale.max,
      };
    }
  }

  submitShotsGraphMinMax(event: KeyboardEvent) {
    if (event.key == "Enter") {
      this.updateShotsGraph();
    }
  }

  transTableTypes(chunk: number) {
    let res = [];
    let size = this.types.length / chunk;
    let idx = 0;
    while (idx < this.types.length) {
      res.push(this.types.slice(idx, idx + size));
      idx += size;
    }
    res.push(this.types.slice(idx, this.types.length));
    return res;
  }

  onKeySwitchTab(event: KeyboardEvent) {
    if (event.key == "ArrowRight") {
      this.selectedPanel = Math.min(
        this.selectedPanel + 1,
        this.panelNames.length - 1
      );
    } else if (event.key == "ArrowLeft") {
      this.selectedPanel = Math.max(0, this.selectedPanel - 1);
    }
  }

  onKeySwitchGraph(event: KeyboardEvent) {
    if (event.key == "ArrowRight") {
      this.onSliceChange(true);
    } else if (event.key == "ArrowLeft") {
      this.onSliceChange(false);
    }
  }

  onCustomNameSelected(name: string) {
    this.customName = name;
    this.updateSelectedShotInfo();
  }

  onRcvrSelected(shotId: number) {
    this.rcvrValue = (
      this.rcvrData.find((s) => s.shotId == shotId) || {}
    ).traces_fit;
  }

  onSynchroniseChange(value) {
    this.storedSettingService.setSynchroniseSettings(value);
    this.storeSettings();
  }

  getShotsOfSameIteration() {
    if (this.shotData) {
      return this.shotData.filter((s) => s.hasTraceFile);
    }
    return [];
  }

  getModelScale(forRcvr = false) {
    let height: number;
    let gridSpace = this.currentJob.modelGrid.dx;
    if (this.is2D) {
      height = -this.currentJob.modelGrid.nx3 * gridSpace;
    } else {
      height = this.currentJob.modelGrid.nx1 * gridSpace;
    }

    return { x: this.currentJob.modelGrid.nx2 * gridSpace, y: height };
  }

  setValueOfSignal(
    event: Event,
    signal_to_set: WritableSignal<number>,
    _default: number = null,
  ) {
    let el = event.target as HTMLInputElement;
    let val = Number(el.value);
    if (!val && _default !== null) {
      // here we set the default, if available for the field
      // this is to allow working with the nzMax scenario
      signal_to_set.set(_default);
      return;
    }
    if (!isNaN(val)) {
      signal_to_set.set(val);
    }
  }

  getParamValue(param) {
    if (!this.selectedBlk) return null;
    switch (this.selectedBlk[param]) {
      case undefined:
      case null:
        return null;
      case "!default":
        return this.defaultMapping.getDefaultValue(param) + " *";
      default:
        return this.selectedBlk[param];
    }
  }

  getShotFuncs(data) {
    if (!data) return null;
    return data.map((d) => d.fnlc);
  }

  getTabName(name) {
    if (
      name != "Difference" ||
      this.shotType.name != "RcvrList" ||
      this.typeOfSelectedRcvr.name != "RcvrList"
    )
      return name;
    else return "Phase Residual";
  }

  openScalingModal() {
    this.showClippingModal = true;
  }

  submitForm() {
    this.wigglePlotting!.submitForm();
    this.getShotImage();
  }

  onVelocityToggled(value: boolean) {
    // used for setting the panelToAuto on teh velocity auto toggle.
    // to begin with, only using it for field and prediction
    if (
      ["Field", "Prediction", "Interleaved"].includes(
        this.panelNames[this.selectedPanel]
      )
    ) {
      this.panelToAuto["Field"] = value;
      this.panelToAuto["Prediction"] = value;
      this.panelToAuto["Interleaved"] = value;
    } else {
      this.panelToAuto[this.panelNames[this.selectedPanel]] = value;
    }
  }

  onScalingInputClick() {
    if (this.velocityAuto) {
      this.velocityAuto = false;
      this.onVelocityToggled(false);
    }
  }

  onScalingInputKeyup(event: KeyboardEvent) {
    if (event.key === "Enter") {
      this.updateMainImage();
    }
  }

  onScalingModalKeyup(event: KeyboardEvent) {
    if (event.key === "Enter") {
      this.getShotImage();
    }
  }

  onScalingInputClickInModal() {
    if (this.velocityAuto) {
      this.velocityAuto = false;
      this.onVelocityToggled(false);
    }
  }

  getShotImage() {
    if (
      ["Field", "Prediction", "Interleaved"].includes(
        this.panelNames[this.selectedPanel]
      )
    ) {
      this.currentScaling = [...this.predictedScaling];

      if (this.globalScaling && this.velocityAuto) {
        this.currentClipping = this.predictedClipping;
      }
    }

    if (this.globalScaling) {
      this.fieldScaling = [...this.predictedScaling];
    }
    this.showClippingModal = false;

    if (this.isPhaseDiff) {
      this.phasediff_clipValue = this.currentScaling[1];
    }

    if (this.globalScaling) {
      for (let name of this.panelNames) {
        this.panelToScaling[name] = this.currentScaling;
        this.panelToAuto[name] = this.velocityAuto;
      }
      if (
        !["Field", "Prediction", "Interleaved"].includes(
          this.panelNames[this.selectedPanel]
        )
      ) {
        this.predictedScaling = [...this.currentScaling];
        this.fieldScaling = [...this.currentScaling];
      }
    } else {
      this.panelToScaling[this.panelNames[this.selectedPanel]] =
        this.currentScaling;
      this.panelToAuto[this.panelNames[this.selectedPanel]] = this.velocityAuto;
    }

    this.syncClipValueForSelectedPanel();

    // force a fresh request here: getTraceImageAndClipValue caches responses by
    // JSON.stringify(parameters), and this is an explicit user submission of new
    // scaling — it must never be answered from a stale cached response for a
    // parameter combination that was already requested earlier in the session.
    this.updateMainImage(true);
    this.storeShotTypeSettings();
  }

  private syncClipValueForSelectedPanel() {
    if (this.isPhaseDiff || this.velocityAuto) return;
    if (this.panelNames[this.selectedPanel] == "Field") {
      this.clipValue = this.fieldScaling;
    } else if (this.panelNames[this.selectedPanel] == "Prediction") {
      this.clipValue = this.predictedScaling;
    } else if (!this.isInterleaveType) {
      this.clipValue = this.currentScaling;
    }
  }

  onChangePredictedScaling() {
    this.predictedScaling[0] = this.predictedScaling[1] * -1 || null;
  }
  onChangePredictedClipping() {
    if (this.globalScaling || this.fieldClipping == null) {
      this.fieldClipping = this.predictedClipping;
    }
  }
  toggleGlobalScaling() {
    this.onChangePredictedScaling();
    this.onChangePredictedClipping();
  }
  morphForClippingModal(clipValues: number[]) {
    // this function is used to morph the clipping values returned from the request (which gets the trace images)
    // to store to the correct varables
    if (this.panelNames[this.selectedPanel] == "Field") {
      this.fieldScaling = clipValues;
    } else if (this.panelNames[this.selectedPanel] == "Prediction") {
      this.predictedScaling = clipValues;
    } else if (this.panelNames[this.selectedPanel] == "Interleaved") {
      if (clipValues.length == 2) {
        this.predictedScaling = Array.from(clipValues);
        this.fieldScaling = Array.from(clipValues);
      } else {
        this.predictedScaling = clipValues.slice(0, 2);
        this.fieldScaling = clipValues.slice(2, 4);
      }
    }
  }

  setScaling() {
    let dialogRef = this.dialog.open(VelocityComponent, {
      height: "350px",
      width: "400px",
      data: {
        singleValue: true,
        min: this.currentScaling[0],
        max: this.currentScaling[1],
        offset: this.currentScaling[1],
        auto: this.velocityAuto,
        isScaling: true,
        global: this.globalScaling,
      },
    });

    dialogRef.afterClosed().subscribe((result: MinMaxDialog) => {
      if (!result) return;
      this.currentScaling = [result.min, result.max];
      this.velocityAuto = result.auto;
      if (!result.auto) {
        this.clipValue = this.currentScaling;
      }
      if (this.isPhaseDiff) {
        this.phasediff_clipValue = this.currentScaling[1];
      }
      if (result.global != null) {
        this.globalScaling = result.global;
      }
      if (this.globalScaling) {
        for (let name of this.panelNames) {
          this.panelToScaling[name] = this.currentScaling;
          this.panelToAuto[name] = this.velocityAuto;
        }
      } else {
        this.panelToScaling[this.panelNames[this.selectedPanel]] =
          this.currentScaling;
        this.panelToAuto[this.panelNames[this.selectedPanel]] =
          this.velocityAuto;
      }
      this.updateMainImage();
      this.storeShotTypeSettings();
    });
  }

  setPhaseClipping = debounce(() => {
    this.currentScaling[1] = this.currentPhaseClipping;
    if (this.globalScaling) {
      for (let name of this.panelNames) {
        this.panelToScaling[name][1] = this.currentPhaseClipping;
        this.panelToAuto[name] = false;
      }
    }
    // this.updateMainImage();
  }, 250);

  // setPhaseScaling() {
  //   let dialogRef = this.dialog.open(VelocityComponent, {
  //     height:"350px",
  //     width:"400px",
  //     data: {
  //       singleValue: true,
  //       min: 0,
  //       max: this.currentPhaseClipping,
  //       offset: this.currentPhaseClipping,
  //       auto: this.default_pdc,
  //     }
  //   })

  //   dialogRef.afterClosed().subscribe((result: MinMaxDialog) => {
  //     if (!result) return;
  //     if (result.auto) {
  //       this.currentPhaseClipping = this.phasediff_clipValue;
  //     } else {
  //       // using single value but we don't want it centered around 0, in fact it shouldn't really matter
  //       // because the lambda for phase plot doesn't care about min anyway
  //       this.currentPhaseClipping = Math.min(result.max, this.phasediff_clipValue);
  //       this.default_pdc = false;
  //     }
  //     this.updateMainImage();
  //     this.storeSettings();
  //   })
  // }

  updateDisplayingRcvr() {
    if (!this.rcvrDataFull) {
      return;
    }
    let criteria;
    if (this.shotType.name == "RcvrList") {
      let selectedIdx = new Set(this.rcvrIndices);
      criteria = function (i) {
        return selectedIdx.has(i);
      };
    } else {
      let startIdx = Math.floor(this.startTrace || 0);
      let endIdx = Math.floor(this.endTrace || 0);
      criteria = function (i) {
        return i >= startIdx && i <= endIdx;
      };
    }
    // decimate first and shallow-copy only the kept entries — deep-cloning
    // the full receiver list froze the main thread on large surveys
    const interval = Math.floor(this.rcvrInterval);
    const displayingData = [];
    if (interval >= 1) {
      // trace numbers are 1-based, rcvrDataFull is 0-based, so +1 to i in criteria 
      for (let i = 0; i < this.rcvrDataFull.length; i += interval) {
        displayingData.push({
          ...this.rcvrDataFull[i],
          hasTraceFile: criteria(i + 1),
        });
      }
    }
    this.rcvrDataDisplay = displayingData;
  }

  updateBgRange() {
    this._minValZ = null;
    this._maxValZ = null;
    this.updateShotsBgImage();
  }

  resetRange() {
    this.rangesStart.x.set(0);
    this.rangesStart.y.set(0);
    this.rangesEnd.x.set(this.modelMax.x);
    this.rangesEnd.y.set(this.modelMax.y);
  }

  private restoreRanges() {
    this.resetRange();
    const lastSettings = this.storedSettingService.getSettings<ShotSettings>(
      this.settingsKey,
      this.currentJob.id,
      SettingTypes.Shot
    );
    const start = lastSettings?.rangesStartKm;
    const end = lastSettings?.rangesEndKm;
    const clamp = (val: number, max: number) =>
      Math.min(max, Math.max(0, val));
    if (start) {
      if (start.x != null) this.rangesStart.x.set(clamp(start.x, this.modelMax.x));
      if (start.y != null) this.rangesStart.y.set(clamp(start.y, this.modelMax.y));
    }
    if (end) {
      if (end.x != null) this.rangesEnd.x.set(clamp(end.x, this.modelMax.x));
      if (end.y != null) this.rangesEnd.y.set(clamp(end.y, this.modelMax.y));
    }
  }

  changeRcvrPlotType(type: ShotType) {
    this.typeOfSelectedRcvr = type;
    this.setPanelNames();
    if (type.name == "RcvrList") return;
    this.delayedLoading = setTimeout(() => (this.loadingImage = true), 250);
    if (
      this.mainImageSubscriber !== null &&
      this.mainImageSubscriber !== undefined
    )
      this.mainImageSubscriber.unsubscribe();
    this.mainImageSubscriber = this.getImageUrl(type, true).subscribe(
      async (res) => {
        if (!res) {
          this.imageUrl = null;
          this.loadingImage = false;
          return;
        }
        clearTimeout(this.delayedLoading);
        this.imageUrl = "data:image/png;base64," + res.imageStr;
        if (this.isPhaseDiff) {
          this.phasediff_clipValue = res.clipValue;
        } else if (this.isSpectrum) {
          this.specMisc(res, this.iteration);
        } else {
          this.clipValue = res.clipValue;
        }
        if (res["corr_mean"]) {
          this.correlation_mean_value = res["corr_mean"];
        }
        if (res.corr) {
          this.corrMisc(res.corr);
        }
        this.loadingImage = false;
      },
      (error) => {
        if (error.status != 404) {
          this.shotImageError = error.detail || error;
        }
        this.imageUrl = null;
        clearTimeout(this.delayedLoading);
        this.loadingImage = false;
      }
    );
  }

  // update functions
  private updateShotsGraph() {
    if (
      this.shotsImageSubscriber !== null &&
      this.shotsImageSubscriber !== undefined
    ) {
      try {
        this.shotsImageSubscriber.unsubscribe();
      } catch (Error) {
        this.shotsImageSubscriber = null;
      }
      this.shotsImageSubscriber = null;
    }
    this.loadingShots = true;
    this.shotsImageSubscriber = this.getIterationShotInfo()
      .pipe(
        finalize(() => {
          if (
            this.shotData &&
            this.selectedShot &&
            this.shotData.some(
              (s) => s.hasTraceFile && s.shotId === this.selectedShot
            )
          ) {
            this.updateSelectedShotInfo();
          } else {
            this.switchIteration = null;
            this.itersOfSameShot = null;
            this.imageUrl = null;
            if (
              this.shotData === null ||
              (this.shotData !== null &&
                this.shotData !== undefined &&
                !this.shotData.map((s) => s.shotId).includes(this.selectedShot)) &&
              this.synchroniseSettings == true
            ) {
              // shot id no longer exists in this job (e.g. shots were renumbered) —
              // fall back to matching the same physical location before giving up
              let matchingShot =
                this.shotData &&
                  this.selectedShotX != null &&
                  this.selectedShotY != null
                  ? this.shotData.find(
                    (s) =>
                      s.hasTraceFile &&
                      s.x === this.selectedShotX &&
                      s.y === this.selectedShotY
                  )
                  : null;
              if (matchingShot) {
                this.selectedShot = matchingShot.shotId;
                this.updateSelectedShotInfo();
                return;
              }
              this.selectedShot = null;
              this.selectedShotX = null;
              this.selectedShotY = null;
            }
            this.loadingImage = false;
          }
        }),
        switchMap((shotsInfo) => {
          let shotsData = this.getShotData(shotsInfo);
          if (!shotsData?.length) {
            return of(null);
          }
          this.shotData = shotsData;
          this.scaleRatio = this.shotData[0].scale_ratio;
          this.availableShots = shotsData.filter((s) => s.hasTraceFile).length;
          this.unavailableShots = shotsData.length;

          if (this.selectedShot) {
            let shot = this.shotData.find((s) => s.shotId == this.selectedShot);
            if (shot) {
              this.selectedTraceFit = [
                Number(shot.traces_fit),
                Number(shot.traces_raw),
              ];
              this.selectedShotLowcut = shot?.lowcut || null;
              this.selectedShotFunctional = Number(
                (shot.fnlc / this.scaleRatio).toFixed(2)
              );
              this.selectedShotX = shot.x;
              this.selectedShotY = shot.y;
            }
          }
          // this.updateMainImage();
          if (!this._maxValZ || !this._minValZ) {
            return this.projectService.getRanges(
              this.jobId,
              "Vp",
              this.currentJob.iterationsComplete,
              { depth: this.sliceNum }
            );
          }
          return of(null);
        }),
        switchMap((range) => {
          if (range) {
            this._minValZ = range.min_val;
            this._maxValZ = range.max_val;
          }
          return this.projectService.getSlice(
            this.jobId,
            "Vp",
            this.iteration - 1,
            3,
            this.sliceNum,
            this.is2D,
            null,
            this._minValZ,
            this._maxValZ
          );
        }),
        catchError((err, caught) => {
          // in the case that the image for the iteration - 1 slice is not found, look for the iteration slice
          return this.projectService.getSlice(
            this.jobId,
            "Vp",
            this.iteration,
            3,
            this.sliceNum,
            this.is2D,
            null,
            this._minValZ,
            this._maxValZ
          );
        }),
        catchError((err) => {
          return of(null);
        }),
        switchMap((plotDetail: PlotDetail) => {
          if (!plotDetail || !plotDetail.url) {
            return of(null);
          }
          return this.imageService.getImageBase64(plotDetail.url);
        })
      )
      .subscribe((sliceImage: string) => {
        this.loadingShots = false;

        this.shotBgImg = sliceImage;
        // this.shotsRef?.resizeBrush(this.selectedRcvrsLimit);
        // this.limitUpdated();
      });
  }

  forceReplot() {
    this.updateMainImage(true);
  }

  private updateMainImage(forceReplot = false) {
    let shotType = this.shotType;
    this.shotImageError = null;
    if (
      this.mainImageSubscriber !== null &&
      this.mainImageSubscriber !== undefined
    ) {
      this.mainImageSubscriber.unsubscribe();
      this.mainImageSubscriber = null;
    }
    clearTimeout(this.delayedLoading);
    if (shotType.name == "RcvrList") {
      if (this.typeOfSelectedRcvr.name == "RcvrList") {
        if (!this.rcvrDataFull) this._getRcvrData(true);
        return;
      } else shotType = this.typeOfSelectedRcvr;
    }

    this.delayedLoading = setTimeout(() => (this.loadingImage = true), 250);

    this.mainImageSubscriber = this.getImageUrl(
      shotType,
      // this.shotType.name == "RcvrList" || this.shotType.name == "SelectRcvrs"
      this.shotType.name == "RcvrList",
      this.iteration,
      (forceReplot = forceReplot)
    ).subscribe(
      (res) => {
        if (!res) {
          clearTimeout(this.delayedLoading);
          this.imageUrl = null;
          this.loadingImage = false;
          return;
        }
        clearTimeout(this.delayedLoading);
        this.imageUrl = "data:image/png;base64," + res.imageStr;
        if (this.isPhaseDiff) {
          this.phasediff_clipValue = res.clipValue;
        } else if (this.isSpectrum) {
          this.specMisc(res, this.iteration);
        } else if (this.velocityAuto){
          if (res.clipValue?.length == 4) {
            this.currentScaling = res.clipValue.slice(0, 2);
          } else if (res.clipValue?.length == 2) {
            this.currentScaling = Array.from(res.clipValue);
          }

          this.panelToScaling[this.panelNames[this.selectedPanel]] = Array.from(
            this.currentScaling
          );
          this.clipValue = Array.from(this.currentScaling);
          this.morphForClippingModal(res.clipValue);
        }
        if (res["corr_mean"]) {
          this.correlation_mean_value = res["corr_mean"];
        }
        if (res.corr) {
          this.corrMisc(res.corr);
        }
        this.updateDisplayingRcvr();
        this.checkPagination();
        this.loadingImage = false;
      },
      (error) => {
        if (error.status != 404) {
          this.shotImageError = error.detail || error;
        }
        this.imageUrl = null;
        clearTimeout(this.delayedLoading);
        this.loadingImage = false;
      }
    );
  }

  onShowWellLogs() {
    if (this.showWellLog) {
      if (this.currentJob.x2_is_inline) {
        this.wellLogs = this.currentJob.wellLogs.map((well) => ({
          x: well.inline * this.currentJob.modelGrid.dx,
          y: well.crossline * this.currentJob.modelGrid.dx,
        }));
      } else {
        this.wellLogs = this.currentJob.wellLogs.map((well) => ({
          x: well.crossline * this.currentJob.modelGrid.dx,
          y: well.inline * this.currentJob.modelGrid.dx,
        }));
      }
    } else this.wellLogs = [];
  }

  private updateSelectedShotInfo() {
    this._getRcvrData(this.shotType.name == "RcvrList");

    if (this.shotType.name == "custom_type" && !this.shotType.options) {
      if (
        this.customNamesSubscriber !== null &&
        this.customNamesSubscriber !== undefined
      )
        this.customNamesSubscriber.unsubscribe();
      this.loadingImage = false;
      this.customNamesSubscriber = this.projectService
        .getAvailableCustomNames(this.jobId, this.iteration, this.selectedShot)
        .subscribe((names) => {
          this.shotType.options = names;
        });
      if (!this.customName) return;
    }
    if (
      this.selectedShotInfoSubscriber !== null &&
      this.selectedShotInfoSubscriber !== undefined
    ) {
      this.selectedShotInfoSubscriber.unsubscribe();
      this.selectedShotInfoSubscriber = null;
    }
    let type =
      // this.shotType.name == "RcvrList" || this.shotType.name == "SelectRcvrs"
      this.shotType.name == "RcvrList" ? "compare" : this.shotType.ttrName;
    let data = { type: type };
    Object.assign(
      data,
      this.iteration && { iteration: this.iteration },
      this.selectedShot && { shot_id: this.selectedShot },
      this.shotType.fwd && { fwd: this.shotType.fwd },
      this.customName && { custom_name: this.customName },
      this.shotType.numPanel && { num_panel: this.shotType.numPanel }
    );

    // if (this.shotType.name == "SelectRcvrs") {
    //   this.projectService
    //     .getTraceMeta(this.jobId, data)
    //     .pipe(
    //       take(1),
    //       switchMap((traceMeta) => {
    //         if (!traceMeta) {
    //           return of(null);
    //         } else {
    //           this.numTraces = traceMeta.num_trace;
    //           this.numSamples = traceMeta.num_sample;
    //           this.totalTime = traceMeta.total_time;
    //           if (this.end_time != null) {
    //             this.end_time = Math.min(this.end_time, this.totalTime);
    //           }
    //           if (!this.startTrace && !this.endTrace) {
    //             this.startTrace = Math.max(
    //               Math.floor((this.numTraces - this.maxNumTracesShown) / 2),
    //               1
    //             );
    //             this.endTrace = Math.min(
    //               this.numTraces,
    //               this.startTrace + this.maxNumTracesShown
    //             );
    //           }
    //           return of(null);
    //         }
    //       })
    //     )
    //     .subscribe({
    //       next: (traceMeta: any) => {
    //         if (!traceMeta) {
    //           return of(null);
    //         }
    //       },
    //       error: (err) => {
    //         console.log("error fetching trace meta", err);
    //       },
    //     });
    //   return of(null);
    // }

    // the trace meta and trace files requests are independent, so fire them
    // in parallel instead of chaining them (saves a full round-trip on every
    // shot/job switch)
    let filesData = {
      type: this.shotType.ttrName,
      shot_id: this.selectedShot,
      custom_name: this.customName,
      fwd: this.shotType.fwd,
    };
    this.selectedShotInfoSubscriber = forkJoin([
      this.projectService.getTraceMeta(this.jobId, data),
      this.projectService.getTraceFilesById(this.jobId, filesData),
    ])
      .pipe(
        switchMap(([traceMeta, files]) => {
          if (!traceMeta) {
            return of(null);
          }
          this.numTraces = traceMeta.num_trace;
          this.numSamples = traceMeta.num_sample;
          this.totalTime = traceMeta.total_time;
          if (this.end_time != null) {
            this.end_time = Math.min(this.end_time, this.totalTime);
          }
          if (
            (!this.startTrace && !this.endTrace) ||
            !(this.endTrace > this.startTrace) ||
            this.startTrace > this.numTraces
          ) {
            this.startTrace = Math.max(
              Math.floor((this.numTraces - this.maxNumTracesShown) / 2),
              1
            );
            this.endTrace = Math.min(
              this.numTraces,
              this.startTrace + this.maxNumTracesShown
            );
          }

          if (!files) {
            return of(null);
          }
          files.sort((a, b) => a - b);
          this.itersOfSameShot = files;
          this.specInit(); // must happen after itersOfSameShot is set
          if (
            this.iteration == this.switchIteration ||
            !this.itersOfSameShot.includes(this.switchIteration)
          ) {
            this.switchIteration = null;
          }
          if (
            this.shotType.name == "RcvrList"
            // this.shotType.name == "RcvrList" ||
            // this.shotType.name == "SelectRcvrs"
          )
            return of(null);
          clearTimeout(this.delayedLoading);
          this.delayedLoading = setTimeout(
            () => (this.loadingImage = true),
            300
          );
          return this.getImageUrl(
            this.shotType,
            // this.shotType.name == "RcvrList" ||
            //   this.shotType.name == "SelectRcvrs"
            this.shotType.name == "RcvrList"
          );
        })
      )
      .subscribe(
        (res) => {
          clearTimeout(this.delayedLoading);
          if (res) {
            this.imageUrl = "data:image/png;base64," + res.imageStr;
            if (this.panelNames[this.selectedPanel] == "Phase Difference") {
              this.phasediff_clipValue = res.clipValue;
            } else if (this.isSpectrum) {
              this.specMisc(res, this.iteration);
            } else if (this.velocityAuto){
              if (res.clipValue?.length == 4) {
                this.currentScaling = res.clipValue.slice(0, 2);
              } else if (res.clipValue?.length == 2) {
                this.currentScaling = Array.from(res.clipValue);
              }
              this.panelToScaling[this.panelNames[this.selectedPanel]] =
                Array.from(this.currentScaling);
              this.clipValue = Array.from(this.currentScaling);
              this.morphForClippingModal(res.clipValue);
            }
            if (res["corr_mean"]) {
              this.correlation_mean_value = res["corr_mean"];
            }
            if (res.corr) {
              this.corrMisc(res.corr);
            }
            this.checkPagination();
          } else {
            this.imageUrl = null;
          }
          this.loadingImage = false;
        },
        (error) => {
          clearTimeout(this.delayedLoading);
          this.imageUrl = null;
          this.loadingImage = false;
          console.log(error);
        }
      );
  }

  private updateShotsBgImage() {
    if (
      this.shotsBgImageSubscriber !== null &&
      this.shotsBgImageSubscriber !== undefined
    ) {
      this.shotsBgImageSubscriber.unsubscribe();
      this.shotsBgImageSubscriber = null;
    }
    this.projectService
      .getRanges(this.jobId, "Vp", this.currentJob.iterationsComplete, {
        depth: this.sliceNum,
      })
      .pipe(
        switchMap((range) => {
          if (!this._minValZ || !this._maxValZ) {
            this._minValZ = range.min_val;
            this._maxValZ = range.max_val;
          }
          return this.projectService.getSlice(
            this.jobId,
            "Vp",
            this.iteration,
            3,
            this.sliceNum,
            this.is2D,
            null,
            null,
            null
          );
        }),
        switchMap((plotDetail: PlotDetail) => {
          if (!plotDetail) return of(null);
          return this.imageService.getImageBase64(plotDetail.url);
        })
      )
      .subscribe((sliceImage: string) => {
        this.shotBgImg = sliceImage;
      });
  }

  private updateFromQueryParams(queryParams: Params) {
    // console.log("updateFromQueryParams")
    if (!this.jobId) return;
    if (!!queryParams["iteration"] || queryParams["iteration"] == 0) {
      this.iteration = parseInt(queryParams["iteration"]) || 1;
    }
    if (!!queryParams["jobs"]) {
      this.selectedJobs = queryParams["jobs"];
    }
    if (!!queryParams["start"]) {
      this.startTrace = Math.max(parseInt(queryParams["start"]), 1);
    }
    if (!!queryParams["end"]) {
      this.endTrace = parseInt(queryParams["end"]);
    }
    if (
      queryParams["type"] &&
      this.types.find((t) => t.name == queryParams["type"])
    ) {
      if (this.shotType.name != queryParams["type"])
        this.shotType = this.types.find((t) => t.name == queryParams["type"]);

      if (queryParams["settings"]) {
        try {
          this.shotType.filterSettings = JSON.parse(queryParams["settings"]);
        } catch (e) {}
      }
    }

    if (queryParams["id"]) {
      this.selectedShot = parseInt(queryParams["id"]);
    }

    this.updateUrl();

    if (
      this.is2D !== null &&
      this.x2IsInline !== null &&
      this.sliceNum !== null &&
      this.nx3 !== null
    ) {
      this.setPanelNames();
      this.updateShotsGraph();
      this.loadedJob = true;
    }
  }

  private updateUrl() {
    this.router.navigate(["."], {
      relativeTo: this.route,
      queryParams: {
        iteration: this.iteration,
        id: this.selectedShot,
        type: this.shotType.name,
        start: this.startTrace,
        end: this.endTrace,
        jobs: this.selectedJobs,
        settings: this.shotType.filterSettings
          ? JSON.stringify(this.shotType.filterSettings)
          : null,
      },
      replaceUrl: true,
    });
  }

  // Request functions
  private getIterationShotInfo(): Observable<
    [Cost, ShotOverlayDto[], number[], ShotOverlayDto[]]
  > {
    return forkJoin([
      this.projectService.getCosts(
        this.jobId,
        this.totalIteration,
        this.x2IsInline
      ),
      this.projectService.getShotOverlay(this.jobId, this.iteration),
      this.projectService.getTraceFilesByIteration(this.jobId, this.iteration),
    ]).pipe(
      // getShotData expects a 4-tuple whose last entry is the same overlay
      // list — reuse the response instead of requesting it twice
      map(
        ([costs, overlays, files]): [
          Cost,
          ShotOverlayDto[],
          number[],
          ShotOverlayDto[]
        ] => [costs, overlays, files, overlays]
      )
    );
  }

  /**
   *
   * @param shotType
   * @param useIdx
   * @param iteration
   * @description get the shot image url
   * the newly added panels, phase difference and spectrum, will set panel to 4 and 5 respectively, the panel values for them are only used to identify the type.
   * the backend and the lambda function will handle these new panels as special cases.
   * the panel values for predicted and field are used to calculate offset in ttr file, so they are set to 0 and 1 respectively.
   * @returns
   */
  private getImageUrl(
    shotType: ShotType,
    useIdx = false,
    iteration = this.iteration,
    forceReplot = false
  ): Observable<any> {
    // the second condition in this of startTrace >= endTrace, is valid.
    // but for 'src' files, there is only 1 trace in the file. As such, we add the != 'Source' condition to continue processing for that edge case.
    if (
      !(iteration && this.selectedShot && this.jobId) ||
      (this.startTrace >= this.endTrace && this.shotType.name != "Source")
      // || this.shotType.name == "SelectRcvrs"
    )
      return of(null);
    this.checkStartTraceEndTrace();
    let startTrace = Math.max(
      Math.floor((this.numTraces - this.maxNumTracesShown) / 2),
      0
    );
    let endTrace = Math.min(
      this.numTraces,
      this.startTrace + this.maxNumTracesShown
    );
    let panel;
    let ob: Observable<any>;
    if (this.panelNames[this.selectedPanel] == "Difference") {
      // the displayed order of difference and interleaved is switched.
      panel = 2;
    } else if (this.panelNames[this.selectedPanel] == "Phase Difference") {
      panel = 4;
    } else if (this.panelNames[this.selectedPanel] == "Spectrum") {
      panel = 5;
      // ob = this.projectService.getFunctionalSequence(this.currentJob.id, this.currentJob.basePath, this.currentJob.prefix)
    } else {
      panel = this.selectedPanel;
    }

    // this is the scaling that will be sent to the backend in the parameters.
    // Nothing special, this just is the if statements to select which one to actually use.
    let appliedScaling = [null, null];
    if (!this.velocityAuto) {
      if (this.panelNames[this.selectedPanel] == "Field") {
        appliedScaling = this.fieldScaling;
      } else if (this.panelNames[this.selectedPanel] == "Prediction") {
        appliedScaling = this.predictedScaling;
      } else if (this.panelNames[this.selectedPanel] == "Interleaved") {
        appliedScaling = [null, null];
      } else {
        appliedScaling = this.currentScaling;
      }
    }

    let appliedClipping = null;
    if (this.velocityAuto) {
      if (this.panelNames[this.selectedPanel] == "Field") {
        appliedClipping = this.fieldClipping;
      } else if (this.panelNames[this.selectedPanel] == "Prediction") {
        appliedClipping = this.predictedClipping;
      } else {
        appliedClipping = this.currentClipping;
      }
    }

    let interleavedScaling = {
      field_scaling: [null, null],
      predicted_scaling: [null, null],
      field_clipping: null,
      predicted_clipping: null,
    };
    if (this.panelNames[this.selectedPanel] == "Interleaved") {
      if (this.velocityAuto == false) {
        interleavedScaling.field_scaling = this.fieldScaling;
        interleavedScaling.predicted_scaling = this.predictedScaling;
      } else {
        interleavedScaling.field_clipping = Math.pow(10, this.fieldClipping);
        interleavedScaling.predicted_clipping = Math.pow(
          10,
          this.predictedClipping
        );
      }
    }

    let parameters = {
      type: shotType.ttrName,
      panel: panel,
      scaling: appliedScaling,
      clipping: Math.pow(10, appliedClipping),
      global_clip: this.globalClipping,
      num_panel: shotType.numPanel,
      color_palette: this.color_palette,
      filter_name:
        shotType.name == "custom" ? this.customShotName : shotType.filterName,
      filter_settings:
        shotType.name == "custom"
          ? JSON.parse(this.customShotSettings || "{}")
          : shotType.filterSettings,
      interleave_attrs: {
        is_interleave: this.isInterleaveType ? 1 : 0,
        extent: this.interleave_extent,
        ...interleavedScaling,
      },
      forceReplot: forceReplot,
    };

    parameters["with_corr"] = this.enable_corr;
    if (this.enable_corr) {
      if (this.corr_start_time) {
        parameters["start_time"] = Math.round(
          this.corr_start_time / (this.totalTime / this.numSamples)
        );
      }

      if (this.corr_end_time) {
        parameters["end_time"] = Math.round(
          this.corr_end_time / (this.totalTime / this.numSamples)
        );
      }
    }

    if (this.panelNames[this.selectedPanel] == "Phase Difference") {
      parameters["scale_fact_power"] = this.phasediff_scale_factor_power;
      parameters["scaling"] = [0, this.currentScaling[1]];
    }

    if (useIdx) {
      let stdSrcLoc = this.shotData.find((s) => s.shotId == this.selectedShot);
      let sortedIndices = this.rcvrData
        .map((r, i) => {
          let distX = r.x - stdSrcLoc.x;
          let distY = r.y - stdSrcLoc.y;
          let dist = distX * Math.abs(distX) + distY * Math.abs(distY);
          // console.log("dist", dist, "rcvrData", r, "stdSrcLoc", stdSrcLoc);
          return { dist: dist, idx: this.rcvrIndices[i] };
        })
        .filter((s) => s.idx != undefined);
      sortedIndices.sort((a, b) => a.dist - b.dist);
      //let sortedIndices = Array.from(this.rcvrIndices)
      //sortedIndices.sort((a, b) => a - b);
      // console.log("sortedIndices", sortedIndices);
      parameters["indices"] = sortedIndices.map((s) => s.idx);
    } else {
      parameters["start_trace"] = this.startTrace || startTrace;
      parameters["end_trace"] = Math.min(
        this.numTraces,
        this.endTrace || endTrace
      );
    }

    if (parameters.filter_settings) {
      parameters.filter_settings.clipping = this.currentClipping;
    }

    Object.assign(
      parameters,
      iteration && { iteration: iteration },
      this.selectedShot && { shot_id: this.selectedShot },
      this.shotType.fwd && { fwd: this.shotType.fwd },
      this.customName && { custom_name: this.customName }
    );

    if (panel == 5) {
      const observable = new Observable<any>((observer) => {
        let params = Object.assign({}, parameters, { iteration: iteration });
        let obs = this.imageService.getTraceImageAndClipValue(
          this.jobId,
          params
        );
        observer.next(obs);
        observer.complete();
      });

      return observable.pipe(
        switchMap((observable: Observable<any>) => observable)
      );
    } else if (this.shotType.independent) {
      // independent types could be anything away from the basic image 'plotting'. case in point is Source
      // mostly to be used in confuction with having a seperate component for these cases
      return of(null);
    } else {
      return this.imageService.getTraceImageAndClipValue(
        this.jobId,
        parameters
      );
    }
  }

  /**
   *
   * @param full_data
   * @description get the receiver data, mainly used for mute plot and spatial plotting. if want mute only, set full_data to false, otherwise set it to true.
   */
  private _getRcvrData(full_data = false) {
    this.rcvrData = [];
    let rcvrType =
      this.shotType.name == "RcvrList"
        ? this.shotType
        : this.types.find((t) => t.name == "RcvrList");
    rcvrType.numPanel = rcvrType.filterSettings.value === "freq" ? 3 : 1;
    let filterParams = {
      angles: rcvrType.filterSettings.angles
        .split(",")
        .map((a: any) => parseInt(a)),
      offset: rcvrType.filterSettings.offset,
      angle_offset: rcvrType.filterSettings.angle_offset,
      value: rcvrType.filterSettings.value,
      // panel: this.selectedPanel,
      panel: 1, // in this given API, the panel is only used for reciever value 'frequency'. as such, in that case we wanna see the field only pretty much ever
      freq: rcvrType.filterSettings.freq,
    };
    this.queryInterval = rcvrType.filterSettings.interval;
    let src_loc0 = null;
    let src_loc1 = null;
    let offset_x1 = null;
    let offset_x2 = null;
    if (this.selectedShot != null) {
      this.projectService
        .getRcvrData(
          this.jobId,
          this.iteration,
          this.selectedShot,
          this.shotType.fwd,
          filterParams,
          this.currentJob.x2_is_inline
        )
        .pipe(
          switchMap((data) => {
            if (!data) {
              this.rcvrData = null;
              this.rcvrDataFull = null;
              this.rcvrValue = 0;
              this.rcvrScale = { min: 0, max: 100 };
              this.inputRcvrScale = { min: 0, max: 100 };
              return;
            }
            // calculate data for mute plots
            let dx = this.currentJob.modelGrid.dx;
            let src = data["src_loc"];
            let rcvr = data["rcvr_loc"];
            let rcvr2src = rcvr.map((r) => {
              return this.euclidean(src[0], r[0], src[1], r[1]);
            });
            this.rcvrDistance = rcvr2src;
            if (src.length == 3 && rcvr[0].length == 3) {
              this.rcvrDistances = [];
              let rcvr2src3d = rcvr.map((r) => {
                return this.euclidean3d(
                  src[0],
                  r[0],
                  src[1],
                  r[1],
                  src[2],
                  r[2]
                );
              });
              this.rcvrDistances.push(rcvr2src3d);
              this.rcvrDistances.push(rcvr2src); // x and y, ignore z
              // assuming [1] to be y
              let rcvr2srcxz = rcvr.map((r) => {
                //x and z, ignore y
                return this.euclidean(src[0], r[0], src[2], r[2]);
              });
              this.rcvrDistances.push(rcvr2srcxz);
              let rcvr2srcyz = rcvr.map((r) => {
                //x and z, ignore y
                return this.euclidean(src[1], r[1], src[2], r[2]);
              });
              this.rcvrDistances.push(rcvr2srcyz);
            } else {
              this.rcvrDistances = [[], rcvr2src, [], []];
            }

            let stdSrcLoc = this.shotData.find(
              (s) => s.shotId == this.selectedShot
            );

            offset_x1 = Math.round(stdSrcLoc.x / dx) * dx - data.src_loc[0];
            if (!this.is2D) {
              offset_x2 = Math.round(stdSrcLoc.y / dx) * dx - data.src_loc[1];
            }

            this.rcvrDataFull = data.rcvr_loc.map((r, i) => {
              return {
                x: r[0] + offset_x1,
                y: r[1] + offset_x2,
                shotId: i + 1,
                hasTraceFile: false,
                traces_fit: 0,
              };
            });
            this.rcvrDataDisplay = this.rcvrDataFull.filter(
              (r, i) => i % this.rcvrInterval === 0
            );
            this.rcvrIndices =
              data.selected_idx || range(0, data.rcvr_loc.length);
            this.rcvrData = this.rcvrDataFull.filter(
              (r, i) =>
                i % rcvrType.filterSettings.interval === 0 &&
                this.rcvrIndices.includes(i)
            );
            src_loc0 = data.src_loc[0];
            src_loc1 = data.src_loc[1];
            if (full_data) {
              try {
                return this.projectService.getRcvrData_data(
                  this.jobId,
                  this.iteration,
                  this.selectedShot,
                  this.shotType.fwd,
                  {...filterParams, interval: rcvrType.filterSettings.interval}
                );
              } catch (e) {
                console.error(e);
                return of(null);
              }
            } else {
              this.updateDisplayingRcvr();

              return of(null);
            }
          })
        )
        .subscribe(
          (data) => {
            if (!data || data.length == 0) {
              this.rcvrAvg = 0;
              this.rcvrStd = 0;
              return;
            }
            this.rcvrAvg = data.reduce((a, b) => a + b, 0) / data.length;
            this.rcvrStd = Math.sqrt(
              data.map((v) => v - this.rcvrAvg).reduce((a, b) => a + b * b, 0) /
              data.length
            );
            if (
              this.currentScaling &&
              this.currentScaling[0] != null &&
              this.currentScaling[1] != null
            ) {
              this.rcvrScale = {
                min: this.currentScaling[0],
                max: this.currentScaling[1],
              };
              this.inputRcvrScale = {
                min: this.currentScaling[0],
                max: this.currentScaling[1],
              };
            } else {
              if (rcvrType.filterSettings.value == "corr") {
                if (this.rcvrScale == null || !this.wasCorr) {
                  this.rcvrScale = { min: -1, max: 1 };
                  this.inputRcvrScale = { min: -1, max: 1 };
                }
                this.wasCorr = true;
              } else if (rcvrType.filterSettings.value == "freq") {
                this.wasCorr = false;
                this.rcvrScale = { min: -Math.PI, max: Math.PI };
                this.inputRcvrScale = { min: -Math.PI, max: Math.PI };
              } else {
                this.wasCorr = false;
                this.rcvrScale = {
                  min: Math.min(...data),
                  max: Math.max(...data),
                };
                this.inputRcvrScale = {
                  min: Math.min(...data),
                  max: Math.max(...data),
                };
              }
              let clipValue = Math.pow(10, this.currentClipping);
              this.rcvrScale = {
                min: this.rcvrScale.min * clipValue,
                max: this.rcvrScale.max * clipValue,
              };
              this.inputRcvrScale = {
                min: this.inputRcvrScale.min * clipValue,
                max: this.inputRcvrScale.max * clipValue,
              };
            }
            
            // the data and teh 'rcvrData' below are both originally wihtout intervals. But the rcvrData has been subsampled in the switchMap above, so the rcvrData now has to also be subsampled and corrected before iterating over it and assigning 
            // const filteredData = data.filter(
            //   (r, i) =>
            //     i % rcvrType.filterSettings.interval === 0 &&
            //     this.rcvrIndices.includes(i)
            // )



            this.rcvrData.forEach((r, i) => (r.traces_fit = data[i]));
            this.rcvrData.push({
              x: src_loc0 + offset_x1,
              y: src_loc1 + offset_x2,
              shotId: 0,
              hasTraceFile: true,
              traces_fit: 0,
            });
            if (rcvrType.filterSettings.value == "corr") {
              this.rcvrColorMap = ["black", "grey", "white"];
            } else {
              this.rcvrColorMap = ["blue", "white", "red"];
            }
            this.calculateTraceRange();
            this.updateDisplayingRcvr();
          },
          (error) => {
            this.rcvrAvg = 0;
            this.rcvrStd = 0;
          }
        );
    }
  }

  euclidean(x1: number, x2: number, y1: number, y2: number) {
    return Math.sqrt(Math.pow(x1 - x2, 2) + Math.pow(y1 - y2, 2));
  }

  euclidean3d(
    x1: number,
    x2: number,
    y1: number,
    y2: number,
    z1: number,
    z2: number
  ) {
    return Math.sqrt(
      Math.pow(x1 - x2, 2) + Math.pow(y1 - y2, 2) + Math.pow(z1 - z2, 2)
    );
  }

  //utility functions
  private getShotData(shotsInfo) {
    if (
      !(
        (
          shotsInfo &&
          shotsInfo[0] &&
          // shotsInfo[1] &&
          shotsInfo[2]
        )
        // shotsInfo[3]
      )
    ) {
      this.shotData = null;
      return;
    }
    let costs: CostItem[] = shotsInfo[0].costItems;
    let shotOverlays: ShotOverlayDto[] = shotsInfo[1];
    let availableShots: number[] = shotsInfo[2] || [];
    let firstShot: ShotOverlayDto[] = shotsInfo[3];
    // shot_fit returns [] rather than null when there's no fit data — treat both
    // as "no overlays" so we fall back to the cost-only shots below
    if (costs && shotOverlays?.length) {
      // index lookups once up-front — find()/includes() inside the map made
      // this O(n²) for large shot counts, and scaleRatio is loop-invariant
      const costByShotNo = new Map(costs.map((c) => [Number(c.shotNo), c]));
      const availableSet = new Set(availableShots);
      const scaleRatio =
        Math.max.apply(
          Math,
          firstShot.map((f) => f.fnlc)
        ) / 100.0;
      return shotOverlays.map((s) => {
        let costItem = costByShotNo.get(Number(s.shot_id));
        let available = availableSet.has(s.shot_id);
        return {
          y: costItem ? (this.is2D ? costItem.sz : costItem.sy) : null,
          x: costItem ? costItem.sx : null,
          shotId: s.shot_id,
          hasTraceFile: available,
          traces_fit: s.traces_fit,
          traces_low: s.dc_low,
          traces_raw: s.dc_raw,
          lowcut: s.lowcut?.match(/\d+/)[0], // we directly only extract the number from the string (4diffns -> 4)
          fnlc: s.fnlc,
          scale_ratio: scaleRatio,
        };
      });
    } else if (costs) {
      const shots = [];
      const availableSet = new Set(availableShots);
      costs.forEach((c) => {
        let available = availableSet.has(c.shotNo);
        let scaleRatio = 1;
        if (available) {
          shots.push({
            y: this.is2D ? c.sz : c.sy,
            x: c.sx,
            shotId: c.shotNo,
            hasTraceFile: available,
            traces_fit: 0,
            lowcut: "",
            traces_raw: 0,
            fnlc: 0,
            scale_ratio: scaleRatio,
          });
        }
      });
      return shots;
    }
  }

  private checkPagination() {
    this.hasRightPage = this.endTrace < this.numTraces;
    this.hasLeftPage = this.startTrace > 1;
  }

  private initPage() {
    this.numSamples = null;
    this.numTraces = null;
    this.totalTime = null;
  }

  private setPanelNames() {
    let numPanel =
      // this.shotType.name == "RcvrList" || this.shotType.name == "SelectRcvrs"
      this.shotType.name == "RcvrList"
        ? this.typeOfSelectedRcvr.numPanel
        : this.shotType.numPanel;
    this.panelNames = this.allPanelNames.slice(0, numPanel);
    if (
      this.shotType.numPanel > 1 &&
      // this.shotType.name != "RcvrList" &&
      // this.shotType.name != "SelectRcvrs"
      this.shotType.name != "RcvrList" &&
      !this.showWiggle
    ) {
      if (this.shotType.displayName != "Compare") {
        this.panelNames.push("Interleaved");
      } else {
        this.panelNames[2] = "Interleaved";
        this.panelNames.push("Difference");
      }
      if (["Raw", "Lowpass", "Compare"].includes(this.shotType.displayName)) {
        this.panelNames.push("Phase Difference");
        this.panelNames.push("Spectrum");
      }
      if (["SelectRcvrs"].includes(this.shotType.name)) {
        this.panelNames.push("Phase Difference");
      }
    } else if (this.showWiggle) {
      if (this.panelNames.includes("Interleaved") || this.panelNames.includes("Difference")) {
        this.panelNames.pop()
      }
    }
    // else if (this.shotType.name == "SelectRcvrs") {
    //   // we want phase differnence in the case of spatial plotting v2
    //   this.panelNames.push("Field");
    //   this.panelNames.push("Interleaved");
    //   this.panelNames.push("Phase Difference");
    // }
    for (let name of this.panelNames) {
      if (this.panelToScaling[name] == null) {
        this.panelToScaling[name] = [null, null];
        this.panelToAuto[name] = true;

        if (this.globalScaling) {
          let otherScales = [];
          let otherAutos = [];
          for (let name2 of this.panelNames) {
            if (name == name2) {
              continue;
            }
            otherScales.push(this.panelToScaling[name2]);
            otherAutos.push(this.panelToAuto[name2]);
          }
          let min = otherScales[0] && otherScales[0][0];
          let max = otherScales[0] && otherScales[0][1];
          let global = true;
          for (let i = 1; i < otherScales.length; i++) {
            if (
              otherScales[i] &&
              (min != otherScales[i][0] || max != otherScales[i][1])
            ) {
              global = false;
              break;
            }
          }
          if (global) {
            this.panelToScaling[name] = [min, max];
            this.panelToAuto[name] = otherAutos[0];
          }
        }
      }
    }
    // precomputed for the mat-tab labels — getTabName() in the template
    // binding would re-run on every change-detection cycle
    this.tabNames = this.panelNames.map((name) => this.getTabName(name));
  }

  private calculateTraceRange() {
    let currentShot = this.shotData.find((s) => s.shotId === this.selectedShot);
    if (
      !currentShot ||
      !this.rcvrDataFull ||
      (this.startTrace && this.endTrace)
    )
      return;
    let rcvrDist = this.rcvrDataFull.map((r, idx) => {
      let dist =
        Math.pow(currentShot.x - r.x, 2) + Math.pow(currentShot.y - r.y, 2);
      // 1-based trace number
      return { dist: dist, idx: idx + 1 };
    });
    rcvrDist = rcvrDist.sort((a, b) => a.dist - b.dist);
    let halfRange = Math.floor(this.maxNumTracesShown / 2);
    this.startTrace =
      this.startTrace != null
        ? this.startTrace
        : Math.max(1, rcvrDist[0].idx - halfRange);
    this.endTrace =
      this.endTrace != null
        ? this.endTrace
        : Math.min(
          this.numTraces || this.startTrace + this.maxNumTracesShown,
          rcvrDist[0].idx + halfRange
        );
    if (this.numTraces != null) {
      this.startTrace = Math.min(
        this.numTraces - this.maxNumTracesShown,
        Math.max(1, this.startTrace)
      );
      this.endTrace = Math.min(
        this.numTraces,
        Math.max(this.maxNumTracesShown, this.endTrace)
      );
    }
    this.updateMainImage();
  }

  private storeSettings() {
    // a batch run passes through jobs without the user visiting them — don't save
    // the batch shot/iteration as those jobs' (or, synchronised, every job's) settings
    if (!this.jobId || this.batchTarget) return;

    // Store current shot type settings before saving
    this.storeShotTypeSettings();

    this.storedSettingService.setSettings<ShotSettings>(
      this.settingsKey,
      {
        id: this.currentJob.id,
        projectId: this.projectId,
        block: this.selectedBlk,
        iteration: this.iteration,
        sliceNum: this.sliceNum,
        shotType: this.shotType,
        selectedShot: this.selectedShot,
        availableShotIds: [],
        startTrace: this.startTrace,
        endTrace: this.endTrace,
        startTraceManual: this.startTraceManual,
        endTraceManual: this.endTraceManual,
        scaling: this.currentScaling,
        fieldScaling: [...this.fieldScaling],
        predictedScaling: [...this.predictedScaling],
        clipping: this.currentClipping,
        fieldClipping: this.fieldClipping,
        predictedClipping: this.predictedClipping,
        velocityAuto: this.velocityAuto,
        color_palette: this.color_palette,
        minValZ: this._minValZ,
        maxValZ: this._maxValZ,
        typeOfSelectedRcvr: this.typeOfSelectedRcvr,
        rcvrIdx: this.rcvrIndices,
        shotGraphHeight: this.shotGraphHeight,
        mainShotGraphHeight: this.mainGraphHeight,
        corr_start_time: this.corr_start_time,
        corr_end_time: this.corr_end_time,
        start_time: this.start_time,
        end_time: this.end_time,
        shot_color: this.shotColor,
        scrollPos: this.scrollPos,
        enable_corr: this.enable_corr,
        mute: this.mute,
        spectrumRange: this.spectrumRange,
        spectrumRangeX: this.spectrumRangeX,
        rangesStartKm: {
          x: this.rangesStart.x(),
          y: this.rangesStart.y(),
        },
        // store an untouched end as null
        rangesEndKm: {
          x: this.rangesEnd.x() === this.modelMax?.x ? null : this.rangesEnd.x(),
          y: this.rangesEnd.y() === this.modelMax?.y ? null : this.rangesEnd.y(),
        },
        shotGraphRadius: this.shotGraphRadius,
        rcvrScale: this.rcvrScale,
        preserveAspectRatio: this.preserveAspectRatio,
        shotGraphWidth: this.shotGraphWidth,
        preserveRcvrAspectRatio: this.preserveRcvrAspectRatio,
        smoothingEnabled: this.smoothingEnabled,
        shotTypeToScale: { ...this.shotTypeToScale },
        shotTypeToAuto: { ...this.shotTypeToAuto },
        shotTypeToPalette: { ...this.shotTypeToPalette },
      },
      SettingTypes.Shot
    );
  }

  private restoreSettings() {
    // console.log("Restoring settings")
    let lastSettings = this.storedSettingService.getSettings<ShotSettings>(
      this.settingsKey,
      this.currentJob.id,
      SettingTypes.Shot,
      this.currentJob.projectId
    );
    if (!lastSettings) {
      return false;
    }

    // Restore shot type scaling maps
    if (lastSettings.shotTypeToScale) {
      this.shotTypeToScale = { ...lastSettings.shotTypeToScale };
    }
    if (lastSettings.shotTypeToAuto) {
      this.shotTypeToAuto = { ...lastSettings.shotTypeToAuto };
    }
    if (lastSettings.shotTypeToPalette) {
      this.shotTypeToPalette = { ...lastSettings.shotTypeToPalette };
    }
    this.iteration = Math.min(
      lastSettings.iteration,
      this.currentJob.iterationsComplete
    );
    this.selectedBlk = lastSettings.block;
    this.shotType = lastSettings.shotType;
    if (this.selectedShotColourType == null) {
      // keep the last colour type
      this.shotTypeToColorType();
    }
    this.selectedShot =
      this.selectedShot != null ? this.selectedShot : lastSettings.selectedShot;
    // this.startTrace = Math.min(this.numTraces-1, lastSettings.startTrace);
    this.startTrace =
      this.startTrace != null ? this.startTrace : lastSettings.startTrace;
    // this.endTrace = Math.min(this.numTraces, lastSettings.endTrace);
    this.endTrace =
      this.endTrace != null ? this.endTrace : lastSettings.endTrace;
    if (this.numTraces != null) {
      this.startTrace = Math.min(
        this.numTraces - this.maxNumTracesShown,
        Math.max(1, this.startTrace)
      );
      this.endTrace = Math.min(
        this.numTraces,
        Math.max(this.maxNumTracesShown, this.endTrace)
      );
    }
    this.currentClipping = lastSettings.clipping || 0;
    this.color_palette =
      (lastSettings.color_palette as any) || "RED_WHITE_BLUE";

    // Restore shot type specific settings
    this.restoreShotTypeSettings();

    // If no shot type specific settings exist, fall back to legacy settings
    if (!this.shotTypeToScale[this.shotType.ttrName]) {
      this.currentClipping = lastSettings.clipping || 0;
      this.currentScaling = lastSettings.scaling || [null, null];
      this.velocityAuto =
        lastSettings.velocityAuto !== undefined
          ? lastSettings.velocityAuto
          : true;
      this.fieldScaling = lastSettings.fieldScaling || [null, null];
      this.predictedScaling = lastSettings.predictedScaling || [null, null];
      this.fieldClipping = lastSettings.fieldClipping || 0;
      this.predictedClipping = lastSettings.predictedClipping || 0;
      this.color_palette =
        (lastSettings.color_palette as any) || "RED_WHITE_BLUE";
      // Store these as shot type specific settings
      this.storeShotTypeSettings();
    }

    setTimeout(() => (this.clipValue = Array.from(this.currentScaling)), 0);
    this.sliceNum = lastSettings.sliceNum;
    this._minValZ = lastSettings.minValZ;
    this._maxValZ = lastSettings.maxValZ;
    if (
      this.shotType.name == "RcvrList" &&
      lastSettings.typeOfSelectedRcvr.name != "RcvrList"
    ) {
      this.rcvrIndices = lastSettings.rcvrIdx;
      this.typeOfSelectedRcvr = lastSettings.typeOfSelectedRcvr;
    }
    this.mainGraphHeight =
      this.mainGraphHeight != this.defaultMainGraphHeight
        ? this.mainGraphHeight
        : lastSettings.mainShotGraphHeight;
    this.shotGraphHeight =
      this.shotGraphHeight != this.defaultShotGraphHeight
        ? this.shotGraphHeight
        : lastSettings.shotGraphHeight;
    if (this.mainGraphHeight == null)
      this.mainGraphHeight = this.defaultMainGraphHeight;
    if (this.shotGraphHeight == null)
      this.shotGraphHeight = this.defaultShotGraphHeight;
    this.corr_start_time =
      this.corr_start_time != null
        ? this.corr_start_time
        : lastSettings.corr_start_time;
    this.corr_end_time =
      this.corr_end_time != null
        ? this.corr_end_time
        : lastSettings.corr_end_time;
    this.start_time =
      this.start_time != null ? this.start_time : lastSettings.start_time;
    this.end_time =
      this.end_time != null ? this.end_time : lastSettings.end_time;
    this.shotColor =
      this.shotColor.min != 0 || this.shotColor.max != 100
        ? this.shotColor
        : lastSettings.shot_color != null
          ? lastSettings.shot_color
          : { min: 0, max: 100 };
    this.updateShotColorRef();
    this.enable_corr =
      lastSettings.enable_corr != null ? lastSettings.enable_corr : false;
    this.mute = lastSettings.mute != null ? lastSettings.mute : false;
    this.spectrumRange = lastSettings.spectrumRange;
    if (this.spectrumRange == null) {
      this.spectrumRange = [null, null];
    }
    this.spectrumRangeX = lastSettings.spectrumRangeX;
    if (this.spectrumRangeX == null) {
      this.spectrumRangeX = [null, null];
    }
    this.shotGraphRadius = lastSettings.shotGraphRadius;

    // Restore preserve aspect ratio setting
    if (lastSettings.preserveAspectRatio != null) {
      this.preserveAspectRatio = lastSettings.preserveAspectRatio;
      // If preserve aspect ratio is enabled, calculate the width based on restored height
      if (this.preserveAspectRatio) {
        setTimeout(() => {
          // Calculate width based on the restored height
          this.calculateWidthFromHeight();
        }, 100);
      } else {
        // Reset width to 100% if aspect ratio is not preserved
        this.shotGraphWidth = "100%";
      }
    }
    if (lastSettings.preserveRcvrAspectRatio != null) {
      this.preserveRcvrAspectRatio = lastSettings.preserveRcvrAspectRatio;
      if (this.preserveRcvrAspectRatio) {
        setTimeout(() => {
          this.calculateRcvrWidthFromHeight();
          this.redrawRcvrGraph();
        }, 100);
      } else {
        this.rcvrGraphWidth = this.defaultRcvrGraphWidth;
      }
    }
    this.smoothingEnabled =
      lastSettings.smoothingEnabled != null
        ? lastSettings.smoothingEnabled
        : false;

    this.scrollPos =
      this.scrollPos != null ? this.scrollPos : lastSettings.scrollPos;
    if (lastSettings.rcvrScale != null) {
      this.rcvrScale = lastSettings.rcvrScale;
      this.inputRcvrScale = {
        min: this.rcvrScale.min,
        max: this.rcvrScale.max,
      };
    }

    return true;
  }

  downloadShotAsPng() {
    const containerElement = document.getElementById("image-and-slice");
    htmlToImage
      .toPng(containerElement, {
        quality: 1.0,
        pixelRatio: window.devicePixelRatio,
        backgroundColor: "#ffffff",
      })
      .then((dataUrl) => {
        let url = dataUrl;
        let name = this.getShotPngNameStr();
        const link = document.createElement("a");
        link.href = url;
        link.download = name;
        link.click();
      })
      .catch((error) => {
        console.error("Error generating screenshot:", error);
      });
  }

  getShotPngNameStr() {
    if (this.shotType.name == "SelectRcvrs") {
      var parts = [
        this.currentJob.projectName.substring(0, 7),
        this.currentJob.name.substring(0, 7),
        this.shotType != null
          ? this.shotType.displayName.substring(0, 5)
          : null,
        this.selectedPanel != null
          ? this.panelNames[this.selectedPanel].substring(0, 5)
          : null,
        this.selectedShot != null
          ? "SHOT" + this.selectedShot.toString()
          : null,
        this.iteration != null ? "CPNUM" + this.iteration.toString() : null,
        this.spatialPlottingV2.boxHeight != null
          ? "HEIGHT(" + this.spatialPlottingV2.boxHeight + ")"
          : null,
        this.spatialPlottingV2.boxWidth != null
          ? "WIDTH(" + this.spatialPlottingV2.boxWidth + ")"
          : null,
        this.spatialPlottingV2.xOffset != null
          ? "XOFF(" + this.spatialPlottingV2.xOffset + ")"
          : null,
        this.spatialPlottingV2.yOffset != null
          ? "YOFF(" + this.spatialPlottingV2.yOffset + ")"
          : null,
        this.spatialPlottingV2.sortingDirection != null
          ? "SORT_DIR(" + this.spatialPlottingV2.sortingDirection + ")"
          : null,
        this.spatialPlottingV2.sortingIndex != null
          ? "SORT_IND(" + this.spatialPlottingV2.sortingIndex + ")"
          : null,
        this.spatialPlottingV2.scaling != null
          ? "SCALE(" + this.spatialPlottingV2.scaling + ")"
          : null,
        this.spatialPlottingV2.rcvrInterval != null
          ? "RCVR_INT(" + this.spatialPlottingV2.rcvrInterval + ")"
          : null,
      ];
    } else {
      var parts = [
        this.currentJob.projectName.substring(0, 7),
        this.currentJob.name.substring(0, 7),
        this.shotType != null
          ? this.shotType.displayName.substring(0, 5)
          : null,
        this.selectedPanel != null
          ? this.panelNames[this.selectedPanel].substring(0, 5)
          : null,
        this.selectedShot != null
          ? "SHOT" + this.selectedShot.toString()
          : null,
        this.currentClipping != null
          ? "CLIP(" + this.currentClipping + ")"
          : null,
        this.iteration != null ? "CPNUM" + this.iteration.toString() : null,
        this.startTrace != null
          ? "TRACE(" +
          this.startTrace.toString() +
          "," +
          this.endTrace.toString() +
          ")"
          : null,
        this.currentScaling != null && this.currentScaling[1] != null
          ? "SCALE(" + this.currentScaling[1].toString() + ")"
          : null,
      ];
    }
    let str = stripDash(parts[0]);
    for (let i = 1; i < parts.length; i++) {
      if (parts[i] == null) continue;
      str += "-" + stripDash(parts[i]);
    }
    return str + "-" + this.currentJob.id + ".png";
  }

  setGlobalScaling() {
    if (this.globalScaling) {
      for (let name of this.panelNames) {
        this.panelToScaling[name] = this.currentScaling;
        this.panelToAuto[name] = this.velocityAuto;
      }
    }
    this.updateMainImage();
  }

  toggleMute() {
    // this.muteOn = [false,false,false]
    this.updateRef();
    this.updateMainImage();
  }

  toggleCorr() {
    if (this.enable_corr && this.corr.length == 0) {
      this.getImageUrl(
        this.shotType,
        this.shotType.name == "Rcvrlist"
      ).subscribe((res) => {
        if (res) {
          if (res["corr_mean"]) {
            this.correlation_mean_value = res["corr_mean"];
          }
          if (res.corr) {
            this.corrMisc(res.corr);
          }
        }
      });
    }
  }

  muteDistanceTypeChanged() {
    this.updateRef();
    this.updateMainImage();
  }

  /**
   *
   * @description updates the references of arrays to trigger rerender of component
   */
  updateRef() {
    this.muteSlope = Array.from(this.muteSlope);
    this.muteIntersection = Array.from(this.muteIntersection);
    this.muteOn = Array.from(this.muteOn);
    this.muteOffset = Array.from(this.muteOffset);
    this.muteDistanceTypes = Array.from(this.muteDistanceTypes);

    // the spectrum dictionaries are mutated in place (specMisc,
    // toggleSpectrumIter), so refresh their references too — the d3-image
    // child compares inputs by reference to skip redundant redraws
    if (this.spectrum) this.spectrum = { ...this.spectrum };
    if (this.spectrumOn) this.spectrumOn = { ...this.spectrumOn };
    if (this.spectrumColors) this.spectrumColors = { ...this.spectrumColors };
    this.spectrumDataType = { ...this.spectrumDataType };
  }

  shotTypeToColorType() {
    if (this.shotType.displayName == "Raw") {
      this.selectedShotColourType = "traces_raw";
    } else {
      this.selectedShotColourType = "traces_fit";
    }
  }

  shotColorTypeName(id: string): string {
    return this.shotColourTypes.find((i) => i.id == id).name;
  }

  updateShotColorRef() {
    this.shotColor = { min: this.shotColor.min, max: this.shotColor.max };
  }

  valueToColor(value: number): string {
    if (value < -1 || value > 1) {
      return `rgb(255,0,0)`;
    }
    let v = Math.round(((value + 1) / 2) * 255);
    return `rgb(${v},${v},${v})`;
  }

  /**
   *
   * @description the visualisation of correlation is a rectangle aligned with the shot image x axis, and the color represents the correlation value
   * used linear gradient and a list of colors to color the rectangle
   */
  updateColorGradient() {
    let colors = this.corr.map((c) => this.color_map(c));
    let gradientColors = "";
    for (let color of colors) {
      gradientColors += color + ", ";
    }
    gradientColors = gradientColors.slice(0, -2);
    this.gradientStyle = {
      background: `linear-gradient(to right, ${gradientColors})`,
      height: "50px",
      "margin-bottom": "15px",
      // "padding": "0",
    };
  }

  /**
   *
   * @description set up correlation variables
   * @param corr
   */
  corrMisc(corr: number[]) {
    let mean = corr.reduce((sum, value) => sum + value, 0) / corr.length;
    let squaredDifferencesSum = corr.reduce(
      (sum, value) => sum + Math.pow(value - mean, 2),
      0
    );
    let std = Math.sqrt(squaredDifferencesSum / corr.length);
    this.corr = corr;
    this.corr_mean = mean;
    this.corr_std = std;
    this.updateColorGradient();
  }

  /**
   *
   * @description initialize spectrum variables
   */
  specInit() {
    //reset colors
    this.availableSpectrumColors = Array.from(this._spectrumColors);
    //init spectrum variables
    this.spectrum = {};
    this.spectrumOn = {};
    this.spectrumColors = {};

    this.spectrumIterOptions = [];
    for (let iter of this.itersOfSameShot) {
      this.spectrumIterOptions.push(iter);
    }
    for (let iter of this.spectrumIterOptions) {
      this.spectrum[iter] = null;
      this.spectrumOn[iter] = [false, false];
      this.spectrumColors[iter] = null;
    }
    this.specInitialized = true;
    this.specInitSubscriber.next(true);
  }

  /**
   * @description set up spectrum variables using the response from the server
   * @param res
   * @param iteration
   */
  specMisc(res: SpectrumResponse, iteration: number) {
    if (this.specInitialized == false) {
      this.specInitSubscriber.pipe(take(1)).subscribe(() => {
        this.specMisc(res, iteration);
      });
      return;
    }
    if (res == null) {
      return;
    }
    let ps = [];
    let fs = [];
    let on;
    let colors;
    on = [true, true];
    if (iteration == this.iteration) {
      colors = Array.from(this.currentIterSpectrumColors);
    } else {
      colors = [this.popColor(), this.popColor()];
    }
    for (let i = 0; i < res.field_x.length; i++) {
      let f = { x: res.field_x[i], y: res.field_y[i] };
      let p = { x: res.predicted_x[i], y: res.predicted_y[i] };
      ps.push(p);
      fs.push(f);
    }
    this.spectrum[iteration] = [ps, fs];
    this.spectrumOn[iteration] = on;
    this.spectrumColors[iteration] = colors;
    this.updateRef();
  }

  /**
   *
   * @description when start time or end time is changed, need to update correlation
   */
  onTimeWindowEmit(event) {
    if (event.start_time == null && event.end_time == null) {
      this.corr_start_time = null;
      this.corr_end_time = null;
    } else {
      this.corr_start_time = Math.min(event.start_time, event.end_time);
      if (this.end_time) {
        this.corr_end_time = Math.min(
          Math.max(event.start_time, event.end_time),
          this.end_time
        );
      } else {
        this.corr_end_time = Math.max(event.start_time, event.end_time);
      }
    }
    this.loadingCorr = true;
    this.getImageUrl(this.shotType, this.shotType.name == "Rcvrlist")
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          if (res) {
            if (res["corr_mean"]) {
              this.correlation_mean_value = res["corr_mean"];
            }
            if (res.corr) {
              this.corrMisc(res.corr);
            }
          }
        },
        error: (err) => (this.loadingCorr = false),
        complete: () => (this.loadingCorr = false),
      });
  }

  downloadPng() {
    resizeBase64Img(
      this.imageUrl,
      this.pngWidth,
      this.pngHeight,
      this.smoothingEnabled
    ).then((resizedImage: string) => {
      const link = document.createElement("a");
      link.href = resizedImage;
      link.download = this.getShotPngNameStr();
      link.click();
    });
  }

  checkStartTimeEndTime() {
    if (this.shotType.name == "Source") {
      if (this.start_time) {
        if (this.start_time < 0) {
          this.start_time = 0;
        }
      }
      if (this.end_time) {
        if (this.end_time > this.totalTime) {
          this.end_time = this.totalTime;
        }
      }
      if (this.end_time && this.start_time && this.start_time > this.end_time) {
        this.start_time = 0;
        this.end_time = this.totalTime;
      }
      this.sourcePlotting.rescale(
        this.end_time || this.totalTime,
        this.start_time || 0
      );
      return;
    }

    if (this.start_time) {
      if (this.start_time < 0) {
        this.start_time = 0;
      }
      if (this.start_time > this.totalTime - 1) {
        this.start_time = this.totalTime - 1;
      }
    }
    if (this.end_time) {
      if (this.end_time > this.totalTime) {
        this.end_time = this.totalTime;
      }
      if (this.end_time < this.start_time + 1) {
        this.end_time = this.start_time + 1;
      }
      if (this.end_time < 1) {
        this.end_time = 1;
      }
    }
  }

  checkStartTraceEndTrace() {
    let startTrace = this.startTrace;
    let endTrace = this.endTrace;
    startTrace = Math.max(startTrace, 1);
    // numTraces is null until the trace meta loads (initPage clears it on every job /
    // iteration change) — Math.min(x, null) is 0, which would wipe out the range
    endTrace = Math.min(endTrace, this.numTraces ?? Infinity);
    if (startTrace > endTrace) {
      startTrace = endTrace - this.maxNumTracesShown;
      if (startTrace < 1) {
        startTrace = 1;
      }
    }
    this.startTrace = startTrace;
    this.endTrace = endTrace;
  }

  onSpectrumDataChanged() {
    this.updateRef();
  }

  checkPhaseDiffScale() {
    if (this.phasediff_scale_factor_power < 0) {
      this.phasediff_scale_factor_power = 2;
    }
    if (this.phasediff_scale_factor_power > 5) {
      this.phasediff_scale_factor_power = 2;
    }
  }

  /**
   *
   * @param index
   * @description toggle spectrum on or off for a particular iteration
   */
  toggleSpectrumIter(index: number) {
    let iter = this.spectrumIterOptions[index];
    this.spectrumOn[iter][0] = !this.spectrumOn[iter][0];
    this.spectrumOn[iter][1] = !this.spectrumOn[iter][1];
    if (this.spectrumOn[iter][0] && this.spectrumOn[iter][1]) {
      if (this.spectrum[iter] == null) {
        this.getImageUrl(this.shotType, this.shotType.name == "Rcvrlist", iter)
          .pipe(take(1))
          .subscribe((res) => {
            if (res) {
              this.specMisc(res, iter);
            }
            this.updateRef();
          });
      } else {
        this.updateRef();
      }
    } else {
      this.updateRef();
    }
  }

  popColor() {
    if (this.availableSpectrumColors.length > 0) {
      return this.availableSpectrumColors.pop();
    } else {
      return getRandomHexColor();
    }
  }

  putColor(color) {
    this.availableSpectrumColors.splice(0, 0, color);
  }

  onIsUpdatingGraphEmit(event) {
    if (event != null) setTimeout(() => (this.is_updating_graph = event), 0);
  }

  onWidthEmit(event) {
    setTimeout(() => (this.shotImageWidth = event), 0);
    // console.log(this.shotImageWidth)
  }

  onSpectrumRangeEmit(event: number[]) {
    if (event.some((item, index) => item != this.spectrumRange[index])) {
      setTimeout(() => (this.spectrumRange = event), 0);
    }
  }

  onSpectrumRangeXEmit(event: number[]) {
    if (event.some((item, index) => item != this.spectrumRangeX[index])) {
      setTimeout(() => (this.spectrumRangeX = event), 0);
    }
  }

  async screenshotPage() {
    try {
      const { name, dataUrl } = await this.capturePage();
      downloadDataUrl(name, dataUrl);
    } catch (error) {
      console.error("Error generating screenshot:", error);
    }
  }

  // Renders the page (or just the main image) to a png, honouring the screenshot
  // toggles. Shared by the Screenshot menu item and batch screenshots.
  private async capturePage(): Promise<CapturedImage> {
    const containerElement = document.getElementById("image-and-slice");
    const target = this.screenshotWithShotsInfo
      ? containerElement
      : (containerElement?.querySelector("#main-image") as HTMLElement | null);
    if (!target) throw new Error("Shot view is not on screen");
    const infoEl =
      this.shotType.name == "SelectRcvrs" && !this.screenshotWithSpatialPlotInfo
        ? (this.spatialPlottingV2.infoContainer.nativeElement as HTMLElement)
        : null;
    if (infoEl) infoEl.style.display = "none";

    try {
      // the frame service re-lays the target out at a screen-independent size (slide shape /
      // fixed width) unless set to 'screen'; the plots here size themselves on window resize
      // the shot plots redraw ~150ms (+50ms) after a resize, so settle for longer than that
      const dataUrl = await this.screenshotFrame.capture(target, {
        notifyWindowResize: true,
        settle: { minWaitMs: 300, stableMs: 300 },
        wrapCapture: withBackgroundSafeRaf,
      });
      const name = this.getShotPngNameStr().replace(/\.png$/, `${this.screenshotFrame.fileTag()}.png`);
      return { name, dataUrl };
    } finally {
      if (infoEl) infoEl.style.display = "grid";
    }
  }

  // ----- Batch Screenshots -----
  openSettings() {
    // mid-run the page is showing a batch job, so keep the options from the job the run started on
    if (!this.batchService.running()) {
      const shotIds: number[] = (this.shotData ?? [])
        .filter((s: any) => s.hasTraceFile)
        .map((s: any) => Number(s.shotId));
      this.batchShotOptions = [...new Set(shotIds)].sort((a, b) => a - b);
      this.batchShotId ??= this.selectedShot;
      if (this.batchJobIds.length === 0 && this.jobId) this.batchJobIds = [this.jobId];
    }
    this.settingsVisible = true;
  }

  useFavouriteJobs() {
    this.batchJobIds = this.favJobsService.favJobs().map((j) => j.id);
  }

  startBatch() {
    if (this.batchShotId == null || this.batchJobIds.length === 0) return;
    this.batchService.runBatch(
      { jobIds: this.batchJobIds, shotId: this.batchShotId, output: this.batchOutput },
      this.batchHost,
    );
  }
  

  private applyBatchTarget() {
    if (!this.batchTarget) return;
    const lastIteration = this.currentJob?.iterationsComplete ?? this.totalIteration;
    this.iteration = Math.min(this.batchTarget.iteration, lastIteration);
    this.selectedShot = this.batchTarget.shotId;
    // no fallback to a shot at the same location: a batch wants this exact id or nothing
    this.selectedShotX = this.batchTarget.x ?? null;
    this.selectedShotY = this.batchTarget.y ?? null;
    // drop the previous job's image/receivers so a load that fails or times out can't be
    // mistaken for this job's shot (see batchHost.showsShot)
    this.imageUrl = null;
    this.rcvrData = null;
  }

  private readonly batchHost: ShotBatchHost = {
    currentJobId: () => this.jobId ?? null,
    currentIteration: () => this.iteration,
    currentShot: () => ({ id: this.selectedShot, x: this.selectedShotX, y: this.selectedShotY }),
    setBatchTarget: (target) => (this.batchTarget = target),
    reloadWithBatchTarget: () => {
      this.applyBatchTarget();
      this.setIterationFrequency();
      this.initPage();
      this.updateShotsGraph();
    },
    isBusy: () => this.loadingShots || this.loadingImage || this.loadingCorr || !!this.is_updating_graph,
    showsShot: (shotId) =>
      this.selectedShot === shotId &&
      !!this.shotData?.some((s) => s.shotId === shotId && s.hasTraceFile) &&
      // the receiver-list view plots receivers without fetching an image; every other view needs the image
      (this.shotType.name == "RcvrList" && this.typeOfSelectedRcvr?.name == "RcvrList"
        ? this.rcvrData?.length > 0
        : this.imageUrl != null),
    capture: () => this.capturePage(),
  };

  updateColorbarLimits(clip_value: any) {
    this.clipValue = [clip_value * -1, clip_value];
  }

  changePalette(
    palette:
      | "RED_WHITE_BLUE"
      | "BLUE_WHITE_RED"
      | "BLACK_AND_WHITE"
      | "WHITE_AND_BLACK"
  ) {
    this.color_palette = palette;
    this.storeShotTypeSettings();
    this.setClipping();
  }

  @HostListener("window:keydown", ["$event"])
  onShiftBKeyDown(event: KeyboardEvent) {
    if (
      event.key.toLowerCase() === "b" &&
      event.shiftKey &&
      this.shotType.name == "SelectRcvrs"
    ) {
      this.selectRcvrs = !this.selectRcvrs;
      this.selectedRcvrsLimit = cloneDeep(this.selectedRcvrsLimit);
    }
  }

  scrollToAnchor(anchor: string) {
    const element = document.getElementById(anchor);
    if (element) {
      const headerOffset = 220; // Change this to whatever looks best
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  }
}
